import LegalAcceptance, { legalPayload, legalReady } from '../../legal/LegalAcceptance';
import useSupportFaq from './useSupportFaq';
import useChatEvents from '../../hooks/useChatEvents';
import usePortalChatReceipts from '../../hooks/usePortalChatReceipts';
import { LegalLinks } from '../LegalDocsPage';
import { pushSession } from '../../mobile/push';
import { navigateToSber } from './sberLogin';
import PhoneCallLogin from './PhoneCallLogin';
import TidLogin from './TidLogin';
import { useMobileSberLogin } from './mobileSberLogin';
import { clearOzonPayment, openOzonPayment, useOzonPaymentStatus, ozonStatusText } from './ozonPayment';
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  Tabs,
  Tab,
  Fade,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Stack,
  Badge,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Telegram as TelegramIcon,
  Sms as SmsIcon,
  Description as ContractIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckIcon,
  LocalShipping as DeliveryIcon,
  Store as StoreIcon,
  ArrowBack as ArrowBackIcon,
  Inventory2 as InventoryIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Extension as ExtendIcon,
  ReceiptLong as ReceiptIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  Send as SendIcon,
  SupportAgent as SupportAgentIcon,
  Storefront as StorefrontIcon,
  ArrowForward as ArrowForwardIcon,
  LockOutlined as LockOutlinedIcon,
  NotificationsActive as NotificationsActiveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import api from '../api/api';
import PickupCodeBlock from './PickupCodeBlock';
import { portalRequest, rememberOrderReturn, pendingOrderReturn, clearOrderReturn } from './portalAccess';
import ExtensionDocuments from '../shared/ExtensionDocuments';
import RentalTerms from '../shared/RentalTerms';
import ClientReturnButton, { HandoverAct, ReturnStatus } from './ClientReturn';
import './ClientPortal.css';

const CHAT_MESSAGE_MAX_LENGTH = 2000;

const AUTH_STEP = 'auth';
const BANK_ID_STEP = 'bank_id';
const CODE_STEP = 'code';
const PORTAL_STEP = 'portal';
const ORDER_CONFIRM_STEP = 'order_confirm';
const ONBOARD_STEP = 'onboard';
const PORTAL_SESSION_KEY = 'clientPortalToken';
const SUPPORT_RETURN_KEY = 'clientSupportReturn';
// Direct order links expose raw deal statuses; the cabinet list normalizes them.
const COMPLETED_ORDER_STATUSES = ['закрыта', 'закрытая', 'завершена', 'completed'];
const RECEIVED_ORDER_STATUSES = ['в_процессе', 'активная', 'выдана', 'active', 'просрочена', ...COMPLETED_ORDER_STATUSES];
const hasConfirmedPayment = data => data?.status === 'оплачена' || RECEIVED_ORDER_STATUSES.includes(data?.status);
const PAYMENT_UNCERTAIN_TEXT = 'Результат оплаты пока неизвестен. Проверяем статус. Не оплачивайте повторно.';

/* Colors */
const PAYMENT_METHOD_NAMES = { cash: 'Наличные', card: 'Карта', transfer: 'Перевод', online: 'Онлайн', ozon: 'Ozon Банк' };

const BRAND = {
  primary: '#a5652c',
  primaryDark: '#8b5223',
  gradient: '#a5652c',
  bg: '#fffdfa',
  card: '#FFFFFF',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  textPrimary: '#0b1634',
  textSecondary: '#657797',
};

// Scoped to the client journey, including dialogs rendered through portals.
const portalTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: BRAND.primary, dark: BRAND.primaryDark },
    secondary: { main: '#555860' },
    background: { default: BRAND.bg, paper: '#ffffff' },
    text: { primary: BRAND.textPrimary, secondary: BRAND.textSecondary },
    divider: '#e8e3dc',
  },
  typography: {
    fontFamily: "'Segoe UI', Arial, sans-serif",
    h5: { fontWeight: 650, letterSpacing: '-0.7px' },
    h6: { fontWeight: 650, letterSpacing: '-0.4px' },
    button: { textTransform: 'none', fontWeight: 600 },
    body2: { lineHeight: 1.7 },
  },
  shape: { borderRadius: 6 },
  components: {
    MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: { root: { borderRadius: 12, minHeight: 44 }, outlined: { borderColor: '#dcd6cc' } } },
    MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 12, backgroundColor: '#fff', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#aaa095' } }, notchedOutline: { borderColor: '#ddd7ce' } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 22, border: '1px solid #e8e3dc', boxShadow: '0 24px 80px #0b163426' } } },
    MuiDialogTitle: { styleOverrides: { root: { padding: '24px 24px 16px' } } },
    MuiAlert: { styleOverrides: { standardInfo: { backgroundColor: '#f5efe6', color: '#705b44', '& .MuiAlert-icon': { color: '#b87945' } } } },
    MuiTab: { styleOverrides: { root: { minWidth: 0, fontSize: 12, fontWeight: 600 } } },
    MuiTabs: { styleOverrides: { indicator: { height: 3, borderRadius: 3 } } },
  },
});

const STATUS_MAP = {
  'заявка': { label: 'Заявка', color: 'warning' },
  'в_процессе': { label: 'Активна', color: 'success' },
  'активная': { label: 'Активна', color: 'success' },
  'оплачена': { label: 'Оплачена', color: 'info' },
  'закрытая': { label: 'Завершена', color: 'default' },
  'просрочена': { label: 'Просрочена', color: 'error' },
  'отменена': { label: 'Отменена', color: 'default' },
};

const formatPickupLocation = (order = {}) => {
  const name = String(order.pickup_point_name || '').trim();
  const address = String(order.pickup_address || order.pickup_point || '').trim();
  return address || name;
};

const BANK_ID_PROVIDERS = {
  tid: { name: 'T-ID', mark: 'T', color: '#ffdd2d', text: '#111111' },
  sber: { name: 'Сбер ID', mark: 'С', color: '#21a038', text: '#ffffff' },
};

const LOGIN_BANK_IDS = [
  // 'tid', // Вернуть после подключения рабочего входа через Т-ID.
  'sber',
];

const DEMO_BANK_IDENTITY = {
  name: 'Иванов Иван Иванович',
  phone: '+7 (900) 000-00-01',
};

function ClientPortal({ showPhoneLogin = false, bankIdProviders = LOGIN_BANK_IDS }) {
  const localDemoEnabled = process.env.NODE_ENV === 'development' &&
    ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
  const navigate = useNavigate();
  const polling = useRef(null);
  const authRequest = useRef(0);
  const orderRequest = useRef(0);
  const chatRequest = useRef(0);
  const chatHistoryRequest = useRef(0);
  const pendingChatMessages = useRef([]);
  const paymentPending = useRef(false);
  const extensionPending = useRef(false);
  const stopTelegramPolling = () => {
    if (polling.current) {
      polling.current.active = false;
      clearInterval(polling.current.interval);
      clearTimeout(polling.current.timeout);
      document.removeEventListener('visibilitychange', polling.current.visibilityHandler);
      polling.current = null;
    }
  };
  useEffect(() => () => {
    stopTelegramPolling();
    authRequest.current += 1;
    orderRequest.current += 1;
    chatRequest.current += 1;
    chatHistoryRequest.current += 1;
    pendingChatMessages.current = [];
  }, []);
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderToken = searchParams.get('order') || token;
  const cartCheckout = searchParams.get('cart') === '1';
  const sberCompletion = searchParams.get('sber') === 'complete';
  const tidCompletion = searchParams.get('tid') === 'complete';
  const pendingItemName = searchParams.get('item'); // товар, выбранный на публичной витрине
  const supportProductValue = Number(searchParams.get('support_product'));
  const supportProductId = Number.isSafeInteger(supportProductValue) && supportProductValue > 0 ? supportProductValue : null;

  const [step, setStep] = useState(AUTH_STEP);
  const [phone, setPhone] = useState('');
  const [callPending, setCallPending] = useState(false);
  const [code, setCode] = useState('');
  const [telegramCode, setTelegramCode] = useState(null);
  const [smsDebugCode, setSmsDebugCode] = useState(null); // код виден, пока SMS-провайдер не подключён
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [codeMethod, setCodeMethod] = useState(null);
  const [checkingTelegram, setCheckingTelegram] = useState(false);
  const [bankIdProvider, setBankIdProvider] = useState(null);
  const [bankIdConsent, setBankIdConsent] = useState(false);
  const [bankIdVerified, setBankIdVerified] = useState(false);
  const [bankDemoNotice, setBankDemoNotice] = useState(null);

  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [contractDialog, setContractDialog] = useState(false);
  const [notClientMessage, setNotClientMessage] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const [portalToken, setPortalToken] = useState(() => sessionStorage.getItem(PORTAL_SESSION_KEY));
  const ozonStatus = useOzonPaymentStatus();
  const rememberPortalToken = (value) => {
    setPortalToken(value || null);
    if (value) sessionStorage.setItem(PORTAL_SESSION_KEY, value);
    else sessionStorage.removeItem(PORTAL_SESSION_KEY);
    if (process.env.REACT_APP_CLIENT_APP === 'true') {
      if (value) localStorage.setItem('mobileClientSession', value);
      else localStorage.removeItem('mobileClientSession');
      if (!value) pushSession('');
    }
  };

  useEffect(() => {
    if (portalToken) {
      sessionStorage.setItem(PORTAL_SESSION_KEY, portalToken);
    }
  }, [portalToken]);
  useEffect(() => {
    if (step === PORTAL_STEP && portalToken) pushSession(portalToken);
  }, [step, portalToken]);
  useEffect(() => {
    if (step !== PORTAL_STEP) return;
    const tab = searchParams.get('tab');
    if (tab === 'chat') setActiveTab(2);
    else if (tab === 'orders') setActiveTab(0);
  }, [step, searchParams]);
  const pendingReturns = orders.filter(o => [o.return_info, ...(o.returns || [])].filter(Boolean)
    .some(info => info.status !== 'completed')).map(o => o.id).join(',');
  const awaitingPickup = orders.some(o => o.status === 'оплачена');
  useEffect(() => {
    if (step !== PORTAL_STEP || !portalToken || (!pendingReturns && !awaitingPickup && ozonStatus !== 'paid')) return;
    let active = true;
    let requestVersion = 0;
    const refresh = async () => {
      const version = ++requestVersion;
      try {
        const res = await api.get('/client-portal/orders', { params: { token: portalToken }, skipLoading: true });
        if (active && version === requestVersion) {
          setOrders(res.data.orders || []);
          if ((res.data.orders || []).some(o => pendingReturns.split(',').includes(String(o.id))
            && o.status === 'закрытая' && [o.return_info, ...(o.returns || [])].filter(Boolean)
              .some(info => info.status === 'completed'))) setActiveTab(1);
        }
      } catch { /* Preserve visible status; the next poll retries. */ }
    };
    if (ozonStatus === 'paid') refresh();
    const timer = setInterval(refresh, 5000);
    const onVisible = () => { if (document.visibilityState !== 'hidden') refresh(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => { active = false; clearInterval(timer); window.removeEventListener('focus', onVisible); document.removeEventListener('visibilitychange', onVisible); };
  }, [step, portalToken, pendingReturns, awaitingPickup, ozonStatus]);
  const [onboardName, setOnboardName] = useState('');
  const [onboardWeeks, setOnboardWeeks] = useState(1);
  const [onboardSubmitting, setOnboardSubmitting] = useState(false);
  const [onboardingToken, setOnboardingToken] = useState(null);
  const [rentalQuote, setRentalQuote] = useState(null);
  const [quoteAttempt, setQuoteAttempt] = useState(0);
  const quoteKey = JSON.stringify([pendingItemName, onboardWeeks, quoteAttempt]);
  const currentQuote = rentalQuote?.key === quoteKey ? rentalQuote : null;
  const quoteReady = !!currentQuote?.data;

  useEffect(() => {
    if (step !== ONBOARD_STEP || !pendingItemName) return undefined;
    const controller = new AbortController();
    let active = true;
    api.get('/client-portal/quote', {
      params: { item_name: pendingItemName, weeks: onboardWeeks },
      signal: controller.signal,
      timeout: 10000,
      skipLoading: true,
    }).then(({ data }) => {
      if (active) setRentalQuote({ key: quoteKey, data });
    }).catch((err) => {
      if (active) setRentalQuote({ key: quoteKey, error: err.response?.data?.detail || 'Не удалось рассчитать стоимость. Попробуйте ещё раз.' });
    });
    return () => { active = false; controller.abort(); };
  }, [step, pendingItemName, onboardWeeks, quoteKey]);

  const [chatMessages, setChatMessages] = useState([]);
  const [operatorPending, setOperatorPending] = useState(false);
  const { items: supportFaq, error: faqError } = useSupportFaq();
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const chatMessagesContainerRef = useRef(null);

  const [pendingOrder, setPendingOrder] = useState(null);
  const [offerAccepted, setOfferAccepted] = useState(false);
  const [loginLegal, setLoginLegal] = useState({});
  const [tidPending, setTidPending] = useState(false);
  const [orderLegal, setOrderLegal] = useState({});
  useEffect(() => { setOrderLegal({}); }, [orderToken]);
  const [pickupCode, setPickupCode] = useState(null);
  const [showContract, setShowContract] = useState(false);
  const [paymentUncertain, setPaymentUncertain] = useState(false);

  const pendingPickup = pendingOrder?.status === 'оплачена' || Boolean(pickupCode);
  useEffect(() => {
    if (step !== ORDER_CONFIRM_STEP || !orderToken || (!pendingPickup && !paymentUncertain && ozonStatus !== 'paid')) return;
    let active = true;
    let requestVersion = 0;
    const refresh = async () => {
      const version = ++requestVersion;
      try {
        const { data } = await api.get(`/client-portal/order/${orderToken}`, portalRequest(portalToken, { skipLoading: true }));
        if (!active || version !== requestVersion) return;
        setPendingOrder(data);
        setPickupCode(data.pickup_code || null);
        if (hasConfirmedPayment(data)) {
          setPaymentUncertain(false);
          setPayCardDialog(false);
          setError(null);
        }
      } catch (err) {
        if (active && version === requestVersion && err.response?.status === 401) requireOrderLogin(orderToken);
      }
    };
    if (ozonStatus === 'paid') refresh();
    const timer = setInterval(refresh, 5000);
    const onVisible = () => { if (document.visibilityState !== 'hidden') refresh(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => { active = false; clearInterval(timer); window.removeEventListener('focus', onVisible); document.removeEventListener('visibilitychange', onVisible); };
  }, [step, orderToken, portalToken, pendingPickup, paymentUncertain, ozonStatus]);

  const [payCardDialog, setPayCardDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('sbp');
  const [missingPaymentRef, setMissingPaymentRef] = useState(null);
  const [noDebitConfirmed, setNoDebitConfirmed] = useState(false);
  useEffect(() => { setMissingPaymentRef(null); setNoDebitConfirmed(false); }, [orderToken]);

  const [extendDialog, setExtendDialog] = useState(false);
  const [extendOrder, setExtendOrder] = useState(null);
  const [extendTariffId, setExtendTariffId] = useState(null);
  const [extendQty, setExtendQty] = useState(1);
  const [extendSelections, setExtendSelections] = useState({});
  const [extensionPaymentMethod, setExtensionPaymentMethod] = useState('sbp');
  const [extensionQuote, setExtensionQuote] = useState(null);
  const extensionSelection = () => Array.isArray(extendOrder?.items) && extendOrder.items.length > 1
    ? { items: extendOrder.items.filter(item => extendSelections[item.asset_id]?.selected).map(item => ({
      asset_id: item.asset_id, tariff_id: extendSelections[item.asset_id].tariff_id, qty: extendSelections[item.asset_id].qty,
    })) } : { tariff_id: extendTariffId, qty: extendQty };
  const extensionSignature = JSON.stringify({ deal: extendOrder?.id, ...extensionSelection() });
  const currentExtensionQuote = extensionQuote?.signature === extensionSignature ? extensionQuote.data : null;

  const editOrderInCatalog = (order, dealId, dealNumber) => {
    const sourceItems = Array.isArray(order?.items) ? order.items : [];
    if (!sourceItems.length || sourceItems.some(item => !item?.showcase_item_id)) {
      setError('Не удалось восстановить товары этой заявки. Обновите страницу или обратитесь в поддержку.');
      return;
    }
    const grouped = new Map();
    sourceItems.forEach((item) => {
      const existing = grouped.get(item.showcase_item_id);
      if (existing) {
        existing.quantity += 1;
        return;
      }
      grouped.set(item.showcase_item_id, {
        showcase_item_id: item.showcase_item_id,
        rental_point_id: item.rental_point_id,
        name: item.name,
        quantity: 1,
        available_quantity: 20,
        weeks: item.weeks || 1,
        min_price: item.amount != null && item.weeks ? Number(item.amount) / Number(item.weeks) : null,
        point_name: order.pickup_point_name,
        point_address: order.pickup_address || order.pickup_point,
      });
    });
    sessionStorage.setItem('storefrontCart', JSON.stringify(Array.from(grouped.values())));
    sessionStorage.setItem('storefrontEditOrder', JSON.stringify({
      deal_id: dealId,
      deal_number: dealNumber || dealId,
    }));
    navigate('/?checkout=1');
  };

  const mobileSber = useMobileSberLogin({
    enabled: process.env.REACT_APP_CLIENT_APP === 'true',
    onComplete: async (value, returnToCart) => {
      setError(null);
      rememberPortalToken(value);
      await checkToken(value, returnToCart, 'login');
    },
    onError: setError,
  });

  useEffect(() => {
    if (sberCompletion || tidCompletion || mobileSber.pending) return;
    if (orderToken) {
      loadPendingOrder(orderToken, portalToken);
    } else {
      orderRequest.current += 1;
      setPaymentUncertain(false);
      if (portalToken) checkToken(portalToken, cartCheckout, 'session');
    }
    return () => { authRequest.current += 1; };
  }, [token, orderToken, cartCheckout, sberCompletion, tidCompletion]);

  useEffect(() => {
    if (!sberCompletion && !tidCompletion) return undefined;
    let active = true;
    setLoading(true); setError(null);
    api.post(`/client-portal/${tidCompletion ? 'tid' : 'sber'}/complete`, {}).then(async ({ data }) => {
      if (!data.token) throw new Error('Login incomplete');
      if (!active) return;
      rememberPortalToken(data.token);
      const destination = await checkToken(data.token, data.return_to_cart, 'login');
      if (active && !data.return_to_cart && destination !== 'order') {
        let target = {};
        try {
          const saved = JSON.parse(sessionStorage.getItem(SUPPORT_RETURN_KEY));
          if (saved && Number.isSafeInteger(saved.id) && saved.id > 0 &&
              Date.now() - saved.created < 15 * 60 * 1000 && Date.now() >= saved.created) {
            target = { tab: 'chat', support_product: String(saved.id), product_name: String(saved.name || '') };
          }
        } catch { /* A missing or expired product choice must not block login. */ }
        sessionStorage.removeItem(SUPPORT_RETURN_KEY);
        setSearchParams(target, { replace: true });
      }
    }).catch(err => {
      if (active) setError(err.response?.data?.detail || 'Не удалось завершить вход через банк. Повторите попытку.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [sberCompletion, tidCompletion]);

  /* --- API --- */

  const requireOrderLogin = (t) => {
    authRequest.current += 1;
    orderRequest.current += 1;
    rememberOrderReturn(t);
    rememberPortalToken(null);
    setClient(null); setOrders([]); setPendingOrder(null); setPickupCode(null);
    setPayCardDialog(false); setExtendDialog(false); setShowContract(false); setLoading(false);
    setStep(AUTH_STEP);
    setError('Войдите, чтобы открыть свой заказ. После входа мы вернём вас к нему.');
  };

  const loadPendingOrder = async (t, session = portalToken) => {
    authRequest.current += 1;
    const version = ++orderRequest.current;
    if (!session) { requireOrderLogin(t); return; }
    try {
      setLoading(true); setError(null);
      setPickupCode(null); setPendingOrder(null); setOfferAccepted(false); setShowContract(false);
      setPayCardDialog(false); setPaymentMethod('sbp');
      setPaymentUncertain(false);
      setStep(ORDER_CONFIRM_STEP);
      const res = await api.get(`/client-portal/order/${t}`, portalRequest(session));
      if (version !== orderRequest.current) return;
      setPendingOrder(res.data);
      setOfferAccepted(res.data.offer_accepted || false);
      if (res.data.pickup_code) setPickupCode(res.data.pickup_code);
      setStep(ORDER_CONFIRM_STEP);
      clearOrderReturn();
    } catch (err) {
      if (version !== orderRequest.current) return;
      if (err.response?.status === 401) { requireOrderLogin(t); return; }
      clearOrderReturn();
      if (err.response?.status === 404) {
        navigate('/my', { replace: true });
        await checkToken(session);
        setError('Заказ не найден в вашем личном кабинете.');
        return;
      }
      setStep(AUTH_STEP);
      setError('Ошибка загрузки заказа');
    } finally { if (version === orderRequest.current) setLoading(false); }
  };

  const acceptOffer = async () => {
    if (!legalReady(orderLegal)) return;
    try {
      setLoading(true); setError(null);
      await api.post(`/client-portal/order/${orderToken}/accept-offer`, { ...legalPayload(orderLegal), order_hash: pendingOrder.order_hash }, portalRequest(portalToken));
      setOfferAccepted(true);
      setPendingOrder(p => ({ ...p, offer_accepted: true }));
    } catch (err) { if (err.response?.status === 401) requireOrderLogin(orderToken); else setError('Ошибка. Попробуйте позже.'); }
    finally { setLoading(false); }
  };

  // A failed HTTP response does not prove that the payment failed to commit.
  const reconcilePayment = async (version) => {
    try {
      const { data } = await api.get(`/client-portal/order/${orderToken}`, portalRequest(portalToken, { skipLoading: true }));
      if (version !== orderRequest.current) return false;
      if (hasConfirmedPayment(data)) {
        setPendingOrder(data);
        setPickupCode(data.pickup_code || null);
        setPaymentUncertain(false);
        setError(null);
        setPayCardDialog(false);
        return true;
      }
    } catch (err) {
      if (version === orderRequest.current && err.response?.status === 401) requireOrderLogin(orderToken);
    }
    return false;
  };

  const checkPayment = async () => {
    if (paymentPending.current) return;
    const version = orderRequest.current;
    paymentPending.current = true;
    setLoading(true);
    try { await reconcilePayment(version); }
    finally {
      paymentPending.current = false;
      if (version === orderRequest.current) setLoading(false);
    }
  };

  const payOrder = async (method = paymentMethod, resumeOnly = false) => {
    if (paymentPending.current) return;
    if (paymentUncertain && !resumeOnly) return checkPayment();
    const version = orderRequest.current;
    paymentPending.current = true;
    try {
      setLoading(true); setError(null);
      const res = await api.post(`/client-portal/order/${orderToken}/pay`, {
        provider: 'yookassa_demo', payment_method: method,
        ...(resumeOnly ? { resume_only: true } : missingPaymentRef && noDebitConfirmed ? { replace_payment_ref: missingPaymentRef } : {}),
      }, portalRequest(portalToken));
      if (version !== orderRequest.current) return;
      setPaymentUncertain(false);
      if (res.data.confirmation_url) {
        openOzonPayment(res.data);
        setPayCardDialog(false);
      } else if (['not_started', 'canceled'].includes(res.data.status)) {
        setPaymentUncertain(false);
        setError(res.data.status === 'not_started' ? 'Оплата ещё не начата. Можно перейти к оплате.' : ozonStatusText.canceled);
      } else if (res.data.status === 'test_paid') {
        setError(ozonStatusText.test_paid);
      } else if (res.data.status === 'not_found') {
        setMissingPaymentRef(res.data.payment_ref);
        setNoDebitConfirmed(false);
        setError(ozonStatusText.not_found);
      } else if (res.data.status === 'order_canceled') {
        clearOzonPayment([res.data.payment_ref]);
        setPayCardDialog(false);
        setError('Заявка отменена.');
        await goToPortal();
      } else if (res.data.status === 'paid_after_cancel') {
        setPayCardDialog(false);
        setError(ozonStatusText.paid_after_cancel);
        await goToPortal();
      } else if (res.data.success) {
        setPickupCode(res.data.pickup_code);
        setPendingOrder(p => ({ ...p, status: 'оплачена', payment_status: 'succeeded', pickup_code: res.data.pickup_code }));
        setPayCardDialog(false);
      } else if (!await reconcilePayment(version) && version === orderRequest.current) {
        setPaymentUncertain(true);
      }
    } catch (err) {
      if (version !== orderRequest.current) return;
      if (err.response?.status === 401) { requireOrderLogin(orderToken); return; }
      if (await reconcilePayment(version) || version !== orderRequest.current) return;
      const status = err.response?.status;
      if (status >= 400 && status < 500 && status !== 408) {
        setError(err.response?.data?.detail || 'Не удалось начать оплату. Проверьте заказ.');
      } else {
        setPaymentUncertain(true);
      }
    } finally {
      paymentPending.current = false;
      if (version === orderRequest.current) setLoading(false);
    }
  };


  // Обновить список заказов (после оплаты появилась новая заявка) и вернуться в обычный кабинет
  const goToPortal = async () => {
    clearOrderReturn();
    if (portalToken) {
      try {
        const res = await api.get('/client-portal/orders', { params: { token: portalToken } });
        setOrders(res.data.orders || []);
      } catch { /* если не удалось обновить — покажем то, что уже было */ }
    }
    navigate('/my', { replace: true });
    setStep(PORTAL_STEP);
  };

  const checkToken = async (candidateToken = portalToken, returnToCart = false, source = 'session') => {
    const version = ++authRequest.current;
    try {
      setLoading(true); setError(null);
      const res = await api.get(`/client-portal/check-token/${candidateToken}`);
      if (version !== authRequest.current) return;
      if (res.data.valid) {
        if (res.data.authenticated && res.data.client) {
          setError(null);
          setClient(res.data.client);
          setOrders(res.data.orders || []);
          rememberPortalToken(candidateToken);
          const returnOrder = source === 'login' && !returnToCart && (orderToken || pendingOrderReturn());
          if (returnOrder) {
            clearOrderReturn();
            if (returnOrder === orderToken) await loadPendingOrder(returnOrder, candidateToken);
            else navigate(`/my?order=${encodeURIComponent(returnOrder)}`, { replace: true });
            return 'order';
          }
          if (returnToCart) navigate('/?checkout=1', { replace: true });
          else {
            setStep(PORTAL_STEP);
            if (token) {
              navigate(`/my${searchParams.toString() ? `?${searchParams}` : ''}`, { replace: true });
            }
          }
        } else {
          setStep(AUTH_STEP); setClient(null); setOrders([]);
          setPhone(res.data.phone_masked || '');
        }
      } else {
        if (candidateToken === sessionStorage.getItem(PORTAL_SESSION_KEY)) rememberPortalToken(null);
        setStep(AUTH_STEP); setClient(null); setOrders([]);
        setError('Сессия завершена. Войдите заново.');
      }
    } catch {
      if (version === authRequest.current) setError('Не удалось проверить вход. Обновите страницу и попробуйте снова.');
    } finally { if (version === authRequest.current) setLoading(false); }
  };

  const requestCode = async (method) => {
    if (!legalReady(loginLegal)) return;
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      setError('Введите корректный номер телефона'); return;
    }
    try {
      setLoading(true); setError(null); setCodeMethod(method);
      const res = await api.post('/client-portal/request-code', {
        phone: phone.replace(/\D/g, ''), method, ...legalPayload(loginLegal),
      });
      if (res.data.success) {
        setStep(CODE_STEP);
        if (method === 'telegram') {
          const m = res.data.message?.match(/код (\d{4})/);
          if (m) setTelegramCode(m[1]);
          startTelegramPolling(res.data.verification_token);
        } else {
          setSmsDebugCode(res.data.debug_code || null);
        }
      } else { setError(res.data.message || 'Ошибка'); }
    } catch (err) { setError(err.response?.data?.detail || 'Ошибка отправки кода'); }
    finally { setLoading(false); }
  };

  const startTelegramPolling = (verificationToken) => {
    stopTelegramPolling();
    setCheckingTelegram(true);
    const cleanPhone = phone.replace(/\D/g, '');
    const attempt = { active: true, inFlight: false };
    polling.current = attempt;
    const poll = async () => {
      if (!attempt.active || attempt.inFlight || document.visibilityState === 'hidden' || window.prokatProstAppPaused) return;
      attempt.inFlight = true;
      try {
        const res = await api.get(`/client-portal/check-verification/${cleanPhone}`, {
          params: { verification_token: verificationToken, ...(pendingItemName ? { item_name: pendingItemName, weeks: onboardWeeks } : {}) },
        });
        if (!attempt.active) return;
        if (res.data.verified) {
          stopTelegramPolling(); setCheckingTelegram(false);
          if (res.data.client_exists === false) {
            setOnboardingToken(res.data.onboarding_token || null);
            if (pendingItemName || cartCheckout) { setStep(ONBOARD_STEP); }
            else { setNotClientMessage(res.data.message); setStep('not_client'); }
          } else {
            setClient(res.data.client); setOrders(res.data.orders || []);
            rememberPortalToken(res.data.token || null);
            if (cartCheckout) { navigate('/?checkout=1', { replace: true }); return; }
            if (res.data.order_token) { setSearchParams({ order: res.data.order_token }); }
            else { setStep(PORTAL_STEP); }
          }
        }
      } catch (err) {
        if (attempt.active && [401, 403].includes(err.response?.status)) {
          stopTelegramPolling();
          setCheckingTelegram(false);
          setStep(AUTH_STEP);
          setError('Попытка входа истекла. Запросите новый код и подтвердите его в Telegram.');
        }
        // Temporary network/server errors can be retried with the same attempt.
      }
      finally { attempt.inFlight = false; }
    };
    attempt.interval = setInterval(poll, 2000);
    attempt.visibilityHandler = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', attempt.visibilityHandler);
    attempt.timeout = setTimeout(() => { stopTelegramPolling(); setCheckingTelegram(false); }, 300000);
  };

  const verifyCode = async () => {
    if (!code || code.length < 4) { setError('Введите код'); return; }
    try {
      setLoading(true); setError(null);
      const res = await api.post('/client-portal/verify-code', {
        phone: phone.replace(/\D/g, ''), code,
        ...(pendingItemName ? { item_name: pendingItemName, weeks: onboardWeeks } : {}),
      });
      if (res.data.success) {
        if (!res.data.client) {
          setOnboardingToken(res.data.onboarding_token || null);
          if (pendingItemName || cartCheckout) { setStep(ONBOARD_STEP); }
          else { setNotClientMessage(res.data.message); setStep('not_client'); }
        } else {
          setClient(res.data.client); setOrders(res.data.orders || []);
          rememberPortalToken(res.data.token || null);
          if (cartCheckout) { navigate('/?checkout=1', { replace: true }); return; }
          if (res.data.order_token) { setSearchParams({ order: res.data.order_token }); }
          else { setStep(PORTAL_STEP); }
        }
      } else { setError('Неверный код'); }
    } catch (err) { setError(err.response?.data?.detail || 'Ошибка'); }
    finally { setLoading(false); }
  };

  const submitOnboard = async () => {
    if (!legalReady(loginLegal)) return;
    if (!onboardName.trim()) { setError('Укажите имя'); return; }
    if (pendingItemName && !quoteReady) return;
    try {
      setOnboardSubmitting(true); setError(null);
      const res = await api.post('/client-portal/onboard', {
        ...legalPayload(loginLegal),
        phone: phone.replace(/\D/g, ''),
        name: onboardName.trim(),
        onboarding_token: onboardingToken,
        item_name: pendingItemName || null,
        weeks: onboardWeeks,
      });
      if (res.data.success) {
        setClient(res.data.client); setOrders(res.data.orders || []);
        rememberPortalToken(res.data.token || null);
        if (cartCheckout) { navigate('/?checkout=1', { replace: true }); return; }
        if (res.data.order_token) { setSearchParams({ order: res.data.order_token }); }
        else { setStep(PORTAL_STEP); }
      } else { setError('Не удалось оформить заявку'); }
    } catch (err) { setError(err.response?.data?.detail || 'Ошибка'); }
    finally { setOnboardSubmitting(false); }
  };

  const loadChatHistory = async ({ silent = false } = {}) => {
    if (!portalToken) return;
    const sessionVersion = chatRequest.current;
    const requestVersion = ++chatHistoryRequest.current;
    try {
      if (!silent) setChatLoading(true);
      const res = await api.get('/client-portal/chat/history', { params: { token: portalToken }, skipLoading: silent });
      if (sessionVersion === chatRequest.current && requestVersion === chatHistoryRequest.current) {
        const serverMessages = res.data.messages || [];
        pendingChatMessages.current = pendingChatMessages.current.filter(local => !serverMessages.some(server => (
          server.direction === local.direction && server.text === local.text
        )));
        setChatMessages([...serverMessages, ...pendingChatMessages.current]);
        setChatUnreadCount(Math.max(0, Number(res.data.unread_count) || 0));
        setOperatorPending(Boolean(res.data.operator_pending));
      }
    } catch { return false; /* Event refresh retries without blocking orders. */ }
    finally {
      if (sessionVersion === chatRequest.current && requestVersion === chatHistoryRequest.current) setChatLoading(false);
    }
  };

  useEffect(() => {
    if (step === PORTAL_STEP && portalToken) { loadChatHistory(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, portalToken]);

  usePortalChatReceipts({ token: step === PORTAL_STEP ? portalToken : null,
    messages: chatMessages, active: step === PORTAL_STEP && activeTab === 2,
    containerRef: chatMessagesContainerRef });

  const chatConnection = useChatEvents('portal', step === PORTAL_STEP ? portalToken : null,
    () => loadChatHistory({ silent: true }));

  useLayoutEffect(() => {
    if (activeTab !== 2) return;
    const container = chatMessagesContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [activeTab, chatMessages.length]);

  const sendChatMessage = async (requestOperator = false) => {
    const text = requestOperator === true ? 'Позовите сотрудника, пожалуйста.' : chatInput.trim();
    if (!text || !portalToken || chatSending) return;
    const version = chatRequest.current;
    const localMessage = { direction: 'in', text, created_at: new Date().toISOString(), localOnly: true };
    pendingChatMessages.current = [...pendingChatMessages.current, localMessage];
    setChatMessages(prev => [...prev, localMessage]);
    setChatInput(''); setChatSending(true);
    try {
        const res = await api.post('/client-portal/chat/send', { token: portalToken, message: text,
          showcase_item_id: supportProductId,
        ...(requestOperator === true ? { request_operator: true } : {}) });
      if (version !== chatRequest.current) return;
      setOperatorPending(Boolean(res.data.operator_pending));
      if (res.data.reply) {
        const localReply = { direction: 'out', text: res.data.reply, created_at: new Date().toISOString(), localOnly: true };
        pendingChatMessages.current = [...pendingChatMessages.current, localReply];
        setChatMessages(prev => [...prev, localReply]);
      }
      await loadChatHistory({ silent: true });
    } catch {
      if (version !== chatRequest.current) return;
      const failureMessage = { direction: 'out', text: 'Не удалось отправить сообщение, попробуйте позже.', created_at: new Date().toISOString(), localOnly: true };
      pendingChatMessages.current = [...pendingChatMessages.current, failureMessage];
      setChatMessages(prev => [...prev, failureMessage]);
    } finally { if (version === chatRequest.current) setChatSending(false); }
  };

  const formatPhone = (value) => {
    const d = value.replace(/\D/g, '');
    if (d.length <= 1) return d.length ? '+7' : '';
    if (d.length <= 4) return `+7 (${d.slice(1)}`;
    if (d.length <= 7) return `+7 (${d.slice(1,4)}) ${d.slice(4)}`;
    if (d.length <= 9) return `+7 (${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
    return `+7 (${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7,9)}-${d.slice(9,11)}`;
  };

  const formatDate = (d) => {
    if (!d) return '\u2014';
    const date = new Date(d);
    return Number.isNaN(date.getTime()) ? '\u2014' : date.toLocaleDateString('ru-RU');
  };

  const logout = () => {
    clearOrderReturn();
    authRequest.current += 1;
    if (portalToken?.startsWith('pc_')) api.post('/client-portal/phone/call/logout', { token: portalToken }, { skipLoading: true }).catch(() => {});
    sessionStorage.removeItem(SUPPORT_RETURN_KEY);
    mobileSber.cancel();
    stopTelegramPolling(); orderRequest.current += 1; chatRequest.current += 1; chatHistoryRequest.current += 1;
    pendingChatMessages.current = [];
    setStep(AUTH_STEP); setClient(null); setOrders([]);
    setPhone(''); setCode(''); setError(null); setSmsDebugCode(null); setTelegramCode(null);
    rememberPortalToken(null); setChatMessages([]); setOperatorPending(false); setOnboardName('');
    setActiveTab(0); setChatInput(''); setChatSending(false); setChatLoading(false); setChatUnreadCount(0);
    setContractDialog(false); setExtendDialog(false); setSelectedOrder(null); setExtendOrder(null);
    setBankIdProvider(null); setBankIdConsent(false); setBankIdVerified(false); setBankDemoNotice(null);
    setLoading(false);
    navigate('/my', { replace: true });
  };

  const openBankIdDemo = (provider) => {
    if (provider === 'sber') {
      if (process.env.REACT_APP_CLIENT_APP === 'true') {
        setLoading(true); setError(null);
        mobileSber.start({ ...legalPayload(loginLegal), return_to_cart: cartCheckout })
          .catch(err => setError(err.response?.data?.detail || 'Не удалось открыть Сбер ID. Повторите попытку.'))
          .finally(() => setLoading(false));
        return;
      }
      setLoading(true); setError(null);
      api.post('/client-portal/sber/start', { ...legalPayload(loginLegal), return_to_cart: cartCheckout })
        .then(({ data }) => {
          sessionStorage.removeItem(SUPPORT_RETURN_KEY);
          if (supportProductId && !cartCheckout) sessionStorage.setItem(SUPPORT_RETURN_KEY, JSON.stringify({
            id: supportProductId, name: searchParams.get('product_name'), created: Date.now(),
          }));
          navigateToSber(data.authorization_url);
        })
        .catch(err => setError(err.response?.data?.detail || 'Не удалось открыть Сбер ID. Повторите попытку.'))
        .finally(() => setLoading(false));
      return;
    }
    setBankIdProvider(provider); setBankIdConsent(false); setBankIdVerified(false);
    setBankDemoNotice(null); setError(null); setStep(BANK_ID_STEP);
  };

  const finishBankIdDemo = async (provider = bankIdProvider) => {
    try {
      setLoading(true); setError(null);
      const res = await api.post('/client-portal/demo-bank-id', { provider, ...legalPayload(loginLegal) });
      setPhone(res.data.client.phone); setOnboardName(res.data.client.name);
      setClient(res.data.client); setOrders(res.data.orders || []);
      rememberPortalToken(res.data.token);
      setBankDemoNotice(null);
      if (cartCheckout) navigate('/?checkout=1', { replace: true });
      else setStep(PORTAL_STEP);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось завершить демонстрационный вход');
    } finally {
      setLoading(false);
    }
  };

  /* ======== AUTH ======== */
  const renderAuth = () => (
    <Fade in>
      <Box className="cp-step-content">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar sx={{ width: 72, height: 72, mx: 'auto', mb: 2, bgcolor: BRAND.primary, fontSize: 32 }}>
            <PersonIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography component="h1" variant="h5" fontWeight={700} color={BRAND.textPrimary}>
            Личный кабинет
          </Typography>
          <Typography variant="body2" color={BRAND.textSecondary} sx={{ mt: 0.5 }}>
            {cartCheckout ? 'Войдите, чтобы оформить заявку. Выбранные товары сохранены в корзине.' : 'Войдите, чтобы просмотреть ваши заказы'}
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e8e3dc' }}>
          <LegalAcceptance value={loginLegal} onChange={setLoginLegal} />
          {localDemoEnabled && <Alert severity="info" sx={{ mb: 2 }}>
            Локальная проверка постамата. Оплата тестовая, деньги не списываются.
            <Button fullWidth variant="outlined" sx={{ mt: 1 }}
              disabled={loading || !legalReady(loginLegal)}
              onClick={() => finishBankIdDemo('tid')}>
              Тестовый вход: Иванов Иван Иванович
            </Button>
          </Alert>}
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <PhoneCallLogin acceptance={legalPayload(loginLegal)} ready={legalReady(loginLegal)}
            disabled={loading || mobileSber.pending || tidPending} returnToCart={cartCheckout} onPending={setCallPending}
            onComplete={async (value, returnToCart) => { setError(null); rememberPortalToken(value); await checkToken(value, returnToCart, 'login'); }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            {bankIdProviders.length === 1 && bankIdProviders[0] === 'sber' && process.env.REACT_APP_ENABLE_TID_LOGIN !== 'true'
              ? 'Вход или регистрация через Сбер ID' : 'Вход или регистрация через банк'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3 }}>
            <TidLogin acceptance={legalPayload(loginLegal)} ready={legalReady(loginLegal)}
              disabled={loading || mobileSber.pending || callPending} returnToCart={cartCheckout} onPending={setTidPending}
              beforeWebLogin={() => {
                sessionStorage.removeItem(SUPPORT_RETURN_KEY);
                if (supportProductId && !cartCheckout) sessionStorage.setItem(SUPPORT_RETURN_KEY, JSON.stringify({
                  id: supportProductId, name: searchParams.get('product_name'), created: Date.now(),
                }));
              }}
              onComplete={async (value, returnToCart) => { setError(null); rememberPortalToken(value); await checkToken(value, returnToCart, 'login'); }} />
            {Object.entries(BANK_ID_PROVIDERS).filter(([key]) => bankIdProviders.includes(key)).map(([key, provider]) => (
              <Button key={key} fullWidth variant="outlined" size="large"
                aria-label={`Войти через ${provider.name}`}
                onClick={() => openBankIdDemo(key)} disabled={loading || mobileSber.pending || tidPending || callPending || !legalReady(loginLegal)}
                startIcon={<Box component="span" sx={{ width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: provider.color, color: provider.text, fontWeight: 900 }}>{provider.mark}</Box>}
                sx={{ py: 1.35, borderRadius: 2, textTransform: 'none', fontWeight: 700, color: BRAND.textPrimary, borderColor: '#dcd6cc', justifyContent: 'flex-start', px: 2.5 }}>
                Войти через {provider.name}
              </Button>
            ))}
          </Box>

          {mobileSber.pending && <Alert severity={mobileSber.networkError ? 'warning' : 'info'} sx={{ mb: 2 }}
            action={<Button color="inherit" onClick={mobileSber.cancel}>Отменить вход</Button>}>
            {mobileSber.networkError ? 'Нет связи с сервером. Проверим вход после восстановления подключения.'
              : 'Завершите вход через Сбер ID в браузере и вернитесь в приложение. Ожидаем подтверждение…'}
          </Alert>}

          {showPhoneLogin && <>
          <Divider sx={{ mb: 3 }}><Typography variant="caption" color={BRAND.textSecondary}>или по номеру телефона</Typography></Divider>

          <TextField
            fullWidth label="Номер телефона" value={phone}
            onChange={(e) => { setPhone(formatPhone(e.target.value)); setError(null); }}
            placeholder="+7 (999) 123-45-67" variant="outlined" type="tel" autoComplete="tel"
            sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1.5, color: BRAND.textSecondary }} /> }}
          />
          </>}

          {bankDemoNotice && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{bankDemoNotice}</Alert>}

          {showPhoneLogin && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button fullWidth variant="contained" size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <TelegramIcon />}
              onClick={() => requestCode('telegram')} disabled={loading || !legalReady(loginLegal)}
              sx={{
                py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600,
                background: BRAND.gradient, '&:hover': { background: BRAND.gradient, opacity: 0.9 },
              }}
            >
              Войти через Telegram
            </Button>

          </Box>}
        </Paper>

        <Typography variant="caption" color={BRAND.textSecondary} sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
          Перед входом прочитайте документы и установите две отдельные отметки.
        </Typography>
      </Box>
    </Fade>
  );

  /* ======== BANK ID VISUAL DEMO ======== */
  const renderBankIdDemo = () => {
    const provider = BANK_ID_PROVIDERS[bankIdProvider] || BANK_ID_PROVIDERS.tid;
    return (
      <Fade in>
        <Box className="cp-step-content">
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, border: '1px solid #e8e3dc' }}>
            <IconButton aria-label="Вернуться к способам входа" onClick={() => { setStep(AUTH_STEP); setBankIdConsent(false); setBankIdVerified(false); }} sx={{ mb: 1 }}>
              <ArrowBackIcon />
            </IconButton>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box sx={{ width: 72, height: 72, mx: 'auto', mb: 2, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: provider.color, color: provider.text, fontSize: 30, fontWeight: 900 }}>{provider.mark}</Box>
              <Chip label="Демонстрационный режим" size="small" sx={{ mb: 1.5, bgcolor: '#f5efe6', color: '#705b44' }} />
              <Typography component="h1" variant="h5" fontWeight={800}>
                {bankIdVerified ? 'Личность подтверждена' : `Вход через ${provider.name}`}
              </Typography>
              <Typography variant="body2" color={BRAND.textSecondary} sx={{ mt: 1 }}>
                {bankIdVerified ? 'Банк передал данные с согласия пользователя' : 'ПрокатПрост получит только данные, необходимые для договора аренды'}
              </Typography>
            </Box>

            {!bankIdVerified ? <>
              <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2, mb: 2.5, bgcolor: '#faf9f7' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Будут переданы</Typography>
                <Stack spacing={1.25}>
                  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}><CheckIcon color="success" fontSize="small" /><Box><Typography variant="body2" fontWeight={600}>Фамилия, имя и отчество</Typography><Typography variant="caption" color={BRAND.textSecondary}>Для заполнения договора</Typography></Box></Box>
                  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}><CheckIcon color="success" fontSize="small" /><Box><Typography variant="body2" fontWeight={600}>Подтверждённый номер телефона</Typography><Typography variant="caption" color={BRAND.textSecondary}>Для связи и входа в кабинет</Typography></Box></Box>
                </Stack>
              </Paper>
              <FormControlLabel control={<Checkbox checked={bankIdConsent} onChange={(event) => setBankIdConsent(event.target.checked)} />}
                label={<Typography variant="body2">Разрешаю передать эти данные сервису «ПрокатПрост»</Typography>} sx={{ alignItems: 'flex-start', mb: 2 }} />
              <Button fullWidth variant="contained" size="large" disabled={!bankIdConsent} onClick={() => setBankIdVerified(true)}
                sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: provider.color, color: provider.text, '&:hover': { bgcolor: provider.color, filter: 'brightness(0.95)' } }}>
                Подтвердить вход
              </Button>
            </> : <>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2.5, bgcolor: '#f7fbf8', borderColor: '#b9ddc2' }}>
                <Stack spacing={2}>
                  <Box><Typography variant="caption" color={BRAND.textSecondary}>ФИО</Typography><Typography variant="body1" fontWeight={700}>{DEMO_BANK_IDENTITY.name}</Typography></Box>
                  <Divider />
                  <Box><Typography variant="caption" color={BRAND.textSecondary}>Телефон</Typography><Typography variant="body1" fontWeight={700}>{DEMO_BANK_IDENTITY.phone}</Typography></Box>
                </Stack>
              </Paper>
              <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>Сейчас это макет: запрос в банк не отправляется. Кнопка создаст локальную демонстрационную сессию.</Alert>
              {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}
              <Button fullWidth variant="contained" size="large" onClick={() => finishBankIdDemo()} disabled={loading}
                sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 700, background: BRAND.gradient }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Продолжить на сайте'}
              </Button>
            </>}
          </Paper>
        </Box>
      </Fade>
    );
  };

  /* ======== CODE ======== */
  const renderCode = () => (
    <Fade in>
      <Box className="cp-step-content">
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e8e3dc' }}>
          <IconButton aria-label="Изменить номер телефона" onClick={() => { stopTelegramPolling(); setStep(AUTH_STEP); setCode(''); setError(null); setTelegramCode(null); setSmsDebugCode(null); setCheckingTelegram(false); }}
            sx={{ mb: 1 }}>
            <ArrowBackIcon />
          </IconButton>

          {codeMethod === 'telegram' ? (
            <>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, bgcolor: BRAND.primary }}>
                  <TelegramIcon sx={{ fontSize: 36 }} />
                </Avatar>
                <Typography variant="h6" fontWeight={700}>Подтверждение через Telegram</Typography>
                <Typography variant="body2" color={BRAND.textSecondary} sx={{ mt: 1 }}>
                  Отправьте код нашему боту
                </Typography>
              </Box>

              {telegramCode && (
                <Paper elevation={0} sx={{
                  textAlign: 'center', p: 3, mb: 3, borderRadius: 2,
                  background: '#fbf2e9',
                  border: '2px dashed #dcaa84',
                }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.primary, letterSpacing: 12, fontFamily: 'monospace' }}>
                    {telegramCode}
                  </Typography>
                </Paper>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="body2" color={BRAND.textSecondary} sx={{ textAlign: 'center' }}>
                  1. Откройте бота &rarr; 2. Отправьте код
                </Typography>
                <Button fullWidth variant="contained" size="large" startIcon={<TelegramIcon />}
                  href="https://t.me/LmedChatBot" target="_blank"
                  sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: BRAND.primary, '&:hover': { bgcolor: BRAND.primaryDark } }}>
                  Открыть бота
                </Button>
              </Box>

              {checkingTelegram && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mt: 3 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color={BRAND.textSecondary}>Ожидаем подтверждение...</Typography>
                </Box>
              )}
            </>
          ) : (
            <>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, bgcolor: BRAND.primary }}>
                  <SmsIcon sx={{ fontSize: 36 }} />
                </Avatar>
                <Typography component="h1" variant="h5" fontWeight={700}>Введите код из SMS</Typography>
                <Typography variant="body2" color={BRAND.textSecondary} sx={{ mt: 1 }}>
                  {smsDebugCode ? 'Вход для номера' : 'Код отправлен на'} {phone}
                </Typography>
              </Box>

              {smsDebugCode && (
                <Paper elevation={0} sx={{
                  textAlign: 'center', p: 3, mb: 3, borderRadius: 2,
                  background: '#fbf2e9',
                  border: '2px dashed #dcaa84',
                }}>
                  <Typography variant="caption" color={BRAND.textSecondary} sx={{ display: 'block', mb: 0.5 }}>
                    Тестовый режим · код для входа:
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.primary, letterSpacing: 12, fontFamily: 'monospace' }}>
                    {smsDebugCode}
                  </Typography>
                </Paper>
              )}

              <TextField fullWidth value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="****"
                sx={{ mb: 3, '& input': { textAlign: 'center', fontSize: 28, letterSpacing: 12, fontFamily: 'monospace' } }}
                inputProps={{ maxLength: 6, inputMode: "numeric", autoComplete: "one-time-code", "aria-label": "Код из SMS" }}
              />

              {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

              <Button fullWidth variant="contained" size="large"
                onClick={verifyCode} disabled={loading || code.length < 4}
                sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600, background: BRAND.gradient }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Подтвердить'}
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Fade>
  );

  /* ======== ONBOARD (новый клиент с витрины) ======== */
  const renderOnboard = () => (
    <Fade in>
      <Box className="cp-step-content">
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Avatar sx={{ width: 72, height: 72, mx: 'auto', mb: 2, bgcolor: BRAND.primary }}>
            <PersonIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography component="h1" variant="h5" fontWeight={700} color={BRAND.textPrimary}>
            Давайте познакомимся
          </Typography>
          <Typography variant="body2" color={BRAND.textSecondary} sx={{ mt: 0.5 }}>
            Вы впервые у нас — напишите ФИО для договора аренды
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e8e3dc' }}>
          <TextField
            fullWidth label="Ваше ФИО" value={onboardName}
            onChange={(e) => { setOnboardName(e.target.value); setError(null); }}
            placeholder="Фамилия Имя Отчество" variant="outlined"
            sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1.5, color: BRAND.textSecondary }} /> }}
          />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="cp-rental-duration-label">Срок аренды</InputLabel>
            <Select labelId="cp-rental-duration-label" value={onboardWeeks} label="Срок аренды" disabled={onboardSubmitting} onChange={(e) => setOnboardWeeks(e.target.value)}>
              {[1, 2, 3, 4].map((w) => (
                <MenuItem key={w} value={w}>{w} {w === 1 ? 'неделя' : w < 5 ? 'недели' : 'недель'}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {pendingItemName && <div className="cp-price-preview" aria-live="polite" aria-atomic="true" aria-busy={!currentQuote}>
            {!currentQuote ? <div className="cp-price-loading"><CircularProgress size={18} /><span>Рассчитываем стоимость…</span></div>
              : currentQuote.error ? <>
                <Typography variant="body2" color="error">{currentQuote.error}</Typography>
                <Button size="small" onClick={() => setQuoteAttempt(value => value + 1)}>Повторить расчёт</Button>
              </> : <>
                <div className="cp-price-total"><span>Стоимость аренды</span><strong>{new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(currentQuote.data.total)}</strong></div>
                <div className="cp-price-detail"><span>За выбранный срок</span><b>{currentQuote.data.requested_days} дн.</b></div>
                <div className="cp-price-detail"><span>Тариф</span><b>{new Intl.NumberFormat('ru-RU').format(currentQuote.data.price_per_period)} ₽ / {currentQuote.data.tariff_days} дн.</b></div>
                {currentQuote.data.billed_days !== currentQuote.data.requested_days && <p className="cp-price-rounding">Аренда оплачивается целыми тарифными периодами. В стоимость включено {currentQuote.data.billed_days} дн.</p>}
                <p className="cp-price-note">Самовывоз · оплата на следующем этапе</p>
              </>}
          </div>}

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

          <Button fullWidth variant="contained" size="large"
            onClick={submitOnboard} disabled={!legalReady(loginLegal) || onboardSubmitting || (pendingItemName && !quoteReady)}
            sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600, background: BRAND.gradient }}>
            {onboardSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Продолжить'}
          </Button>
        </Paper>

        <Typography variant="caption" color={BRAND.textSecondary} sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
          После этого мы свяжемся с вами через чат в личном кабинете, чтобы подтвердить аренду и оплату
        </Typography>
      </Box>
    </Fade>
  );

  /* ======== PORTAL ======== */
  const activeOrders = orders.filter(o => ['в_процессе', 'активная', 'заявка', 'оплачена', 'просрочена'].includes(o.status) || (o.return_info && o.return_info.status !== 'completed'));
  const closedOrders = orders.filter(o => ['закрытая', 'завершена', 'отменена'].includes(o.status) && (!o.return_info || o.return_info.status === 'completed'));

  const renderChat = () => (
    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8e3dc', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <SupportAgentIcon sx={{ color: BRAND.primary }} />
        <Typography variant="h6" fontWeight={700}>Чат поддержки</Typography>
      </Box>
      <Typography variant="body2" color={BRAND.textSecondary} sx={{ mb: 2 }}>
        {operatorPending ? 'Диалог передан сотруднику. Помощник отключён; ответ появится здесь.'
          : 'Отвечает ИИ-помощник по правилам сервиса. При необходимости можно позвать сотрудника.'}
      </Typography>
      {['reconnecting', 'unauthorized'].includes(chatConnection) && <Alert severity="warning" sx={{ mb: 2 }}>
        {chatConnection === 'unauthorized' ? 'Сессия чата завершена. Войдите снова.' : 'Связь с чатом прервана. Восстанавливаем соединение…'}
      </Alert>}
      {!operatorPending && <Button variant="outlined" size="small" sx={{ mb: 2 }}
        disabled={chatSending} onClick={() => sendChatMessage(true)}>Позвать сотрудника</Button>}
      {supportProductId && <Alert severity="info" sx={{ mb: 2 }} action={<Button disabled={chatSending} onClick={() => {
        const params = new URLSearchParams(searchParams); params.delete('support_product'); params.delete('product_name'); setSearchParams(params, { replace: true });
      }}>Сменить товар</Button>}>
        Вопрос о товаре: {searchParams.get('product_name') || `позиция ${supportProductId}`}.
      </Alert>}
      <Box ref={chatMessagesContainerRef} data-testid="client-chat-messages" sx={{
        maxHeight: 340, minHeight: 120, overflowY: 'auto', display: 'flex', flexDirection: 'column',
        gap: 1, mb: 2, p: 1.5, bgcolor: '#f7f5f1', borderRadius: 2,
      }}>
        {chatLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
        ) : chatMessages.length === 0 ? (
          <Typography variant="body2" color={BRAND.textSecondary} sx={{ textAlign: 'center', py: 2 }}>
            Сообщений пока нет — начните диалог ниже.
          </Typography>
        ) : (
          chatMessages.map((m, i) => (
            <Box key={m.id || i} data-chat-message-id={m.direction === 'out' ? m.id : undefined} sx={{
              alignSelf: m.direction === 'in' ? 'flex-end' : 'flex-start',
              bgcolor: m.direction === 'in' ? BRAND.primary : '#fff',
              color: m.direction === 'in' ? '#fff' : BRAND.textPrimary,
              border: m.direction === 'in' ? 'none' : '1px solid #e8e3dc',
              borderRadius: 2, px: 1.5, py: 1, maxWidth: '80%',
            }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.text}</Typography>
            </Box>
          ))
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <TextField
          fullWidth size="small" placeholder="Напишите сообщение..." value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !chatSending) {
              e.preventDefault();
              sendChatMessage();
            }
          }}
          multiline minRows={1} maxRows={5}
          inputProps={{ maxLength: CHAT_MESSAGE_MAX_LENGTH }}
          helperText={`${chatInput.length}/${CHAT_MESSAGE_MAX_LENGTH}`}
        />
        <Button
          aria-label="Отправить сообщение"
          variant="contained" onClick={() => sendChatMessage()} disabled={chatSending || !chatInput.trim()}
          sx={{ borderRadius: 2, textTransform: 'none', background: BRAND.gradient, minWidth: 48 }}
        >
          {chatSending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
        </Button>
      </Box>
    </Paper>
  );

  const renderFaq = () => (
    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8e3dc', overflow: 'hidden' }}>
      <Typography variant="h6" fontWeight={700} sx={{ p: 2.5, pb: 1 }}>Частые вопросы</Typography>
      <Button component={RouterLink} to="/help" sx={{ mx: 1.5, mb: 1 }}>Инструкции со скриншотами</Button>
      {faqError && <Alert severity="info">Не удалось загрузить правила. Обновите страницу или напишите в чат поддержки.</Alert>}
      {!faqError && !supportFaq.length && <Typography sx={{ p: 2 }}>Загрузка правил…</Typography>}
      {supportFaq.map((item, i) => (
        <Accordion key={i} elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600} variant="body2">{item.q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color={BRAND.textSecondary}>{item.a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );

  const renderPortal = () => (
    <Fade in>
      <Box>
        <Paper elevation={0} sx={{
          p: 3, mb: 3, borderRadius: 3,
          background: '#0b1634', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 24, fontWeight: 700 }}>
              {(client?.name || '\u041A')[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>{client?.name || 'Клиент'}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>{client?.phone || ''}</Typography>
            </Box>
          </Box>
          <IconButton aria-label="Выйти из кабинета" onClick={logout} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
            <LogoutIcon />
          </IconButton>
        </Paper>

        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8e3dc', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth"
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
            <Tab icon={<InventoryIcon />} label={`Активные (${activeOrders.length})`} />
            <Tab icon={<TimeIcon />} label={`Завершённые (${closedOrders.length})`} />
            <Tab
              aria-label={chatUnreadCount ? `Чат, новых сообщений: ${chatUnreadCount}` : 'Чат'}
              icon={
                <Badge color="error" badgeContent={chatUnreadCount} max={99}>
                  {chatUnreadCount ? <NotificationsActiveIcon /> : <SupportAgentIcon />}
                </Badge>
              }
              label="Чат"
            />
            <Tab icon={<ExpandMoreIcon />} label="Вопросы" />
          </Tabs>
        </Paper>

        {error && !extendDialog && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {activeTab === 0 && renderOrderList(activeOrders, true)}
        {activeTab === 1 && renderOrderList(closedOrders, false)}
        {activeTab === 2 && renderChat()}
        {activeTab === 3 && renderFaq()}

        <Dialog open={contractDialog} onClose={() => setContractDialog(false)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>
            Договор аренды {selectedOrder?.deal_number || selectedOrder?.id}
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" role="note" sx={{ mb: 2 }}>
              Это первоначальный текст договора. Фактическую выдачу смотрите в акте,
              а последующие продления — в подтверждениях ниже. Текущий срок заказа:
              {' '}{formatDate(selectedOrder?.start_date)} — {formatDate(selectedOrder?.end_date)}.
            </Alert>
            {selectedOrder?.contract_source === 'unavailable' || (selectedOrder?.offer_accepted && !selectedOrder?.contract_text) ? (
              <Alert severity="warning">
                Сохранённая копия принятого договора недоступна. Обратитесь в пункт проката за документом.
              </Alert>
            ) : <>
            {selectedOrder?.contract_source === 'accepted_snapshot' && (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
                Сохранённая редакция договора{selectedOrder.offer_version_number != null ? ` №${selectedOrder.offer_version_number}` : ''}
                {selectedOrder.contract_accepted_at ? `. Принят: ${new Date(selectedOrder.contract_accepted_at).toLocaleString('ru-RU')}` : ''}
              </Typography>
            )}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
              {selectedOrder?.contract_text || `Договор аренды оборудования.\n\nТовар: ${selectedOrder?.asset_name || '—'}\nПериод: ${formatDate(selectedOrder?.start_date)} — ${formatDate(selectedOrder?.end_date)}\nСумма: ${selectedOrder?.total_amount ?? selectedOrder?.amount_due ?? 0} ₽\n\nУсловия стандартные.`}
            </Typography>
            </>}
            <ExtensionDocuments documents={selectedOrder?.extensions} />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setContractDialog(false)} variant="contained" sx={{ borderRadius: 2, textTransform: 'none' }}>
              Закрыть
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={extendDialog} onClose={() => setExtendDialog(false)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>Продлить аренду</DialogTitle>
          <DialogContent>
            {extendOrder && (() => {
              const items = Array.isArray(extendOrder.items) && extendOrder.items.length > 1 ? extendOrder.items : null;
              if (!items) {
                const tariffs = extendOrder.tariffs || [];
                const selectedTariff = tariffs.find(t => t.id === extendTariffId) || null;
                const totalPrice = selectedTariff ? selectedTariff.price * extendQty : 0;
                let newEndDate = null;
                if (selectedTariff && extendOrder.end_date) {
                  newEndDate = new Date(new Date(extendOrder.end_date).getTime() + selectedTariff.duration_days * extendQty * 86400000);
                }
                return <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color={BRAND.textSecondary} sx={{ mb: 2 }}>{extendOrder.asset_name}</Typography>
                  {!tariffs.length ? <Typography variant="body2" color="error" sx={{ mb: 2 }}>Для этого товара не настроены тарифы. Обратитесь к менеджеру.</Typography> : <>
                    {tariffs.length === 1 ? <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 2 }}><Typography variant="caption" color={BRAND.textSecondary}>Тариф (фиксированный)</Typography><Typography variant="body2" fontWeight={700}>{tariffs[0].name} — {tariffs[0].price} ₽</Typography></Paper> : <FormControl fullWidth sx={{ mb: 2 }}><InputLabel id="cp-extend-tariff">Тариф</InputLabel><Select labelId="cp-extend-tariff" disabled={loading} value={extendTariffId || ''} onChange={(e) => setExtendTariffId(e.target.value)} label="Тариф">{tariffs.map(t => <MenuItem key={t.id} value={t.id}>{t.name} — {t.price} ₽</MenuItem>)}</Select></FormControl>}
                    <FormControl fullWidth sx={{ mb: 2 }}><InputLabel id="cp-extend-quantity">Количество</InputLabel><Select labelId="cp-extend-quantity" disabled={loading} value={extendQty} onChange={(e) => setExtendQty(e.target.value)} label="Количество">{[1,2,3,4,5,6,7,8,9,10].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}</Select></FormControl>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#f7f5f1', borderRadius: 2, border: '1px solid #e8e3dc' }}><Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography variant="body2" color={BRAND.textSecondary}>Аренда до:</Typography><Typography variant="body2" fontWeight={600}>{newEndDate ? newEndDate.toLocaleDateString('ru-RU') : '—'}</Typography></Box><Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">К оплате:</Typography><Typography variant="h6" fontWeight={700} color={BRAND.primary}>{totalPrice} ₽</Typography></Box></Paper>
                  </>}
                </Box>;
              }
              const selected = items.filter(item => extendSelections[item.asset_id]?.selected);
              const total = selected.reduce((sum, item) => {
                const choice = extendSelections[item.asset_id];
                const tariff = (item.tariffs || []).find(t => t.id === choice.tariff_id);
                return sum + Number(tariff?.price || 0) * Number(choice.qty || 1);
              }, 0);
              return <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color={BRAND.textSecondary} sx={{ mb: 2 }}>Отметьте товары, которые нужно продлить. Остальные останутся без изменений.</Typography>
                <Stack spacing={1.5}>{items.map(item => {
                  const tariffs = item.tariffs || [];
                  const choice = extendSelections[item.asset_id] || {};
                  const tariff = tariffs.find(t => t.id === choice.tariff_id);
                  const newEnd = tariff && item.end_date ? new Date(new Date(item.end_date).getTime() + tariff.duration_days * (choice.qty || 1) * 86400000) : null;
                  return <Paper key={item.asset_id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <FormControlLabel control={<Checkbox checked={Boolean(choice.selected)} disabled={!tariffs.length || item.return_info} onChange={event => setExtendSelections(prev => ({ ...prev, [item.asset_id]: { ...choice, selected: event.target.checked } }))} />} label={<Typography fontWeight={650}>{item.name}</Typography>} />
                    {!tariffs.length && <Typography display="block" variant="caption" color="error">Для товара не настроен тариф</Typography>}
                    {item.return_info && <Typography display="block" variant="caption" color="text.secondary">По товару уже оформлен возврат</Typography>}
                    {choice.selected && tariffs.length > 0 && <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}><InputLabel id={`tariff-${item.asset_id}`}>Тариф</InputLabel><Select labelId={`tariff-${item.asset_id}`} aria-label={`Тариф для ${item.name}`} value={choice.tariff_id || ''} label="Тариф" onChange={event => setExtendSelections(prev => ({ ...prev, [item.asset_id]: { ...choice, tariff_id: event.target.value } }))}>{tariffs.map(t => <MenuItem key={t.id} value={t.id}>{t.name} — {t.price} ₽</MenuItem>)}</Select></FormControl>
                      <FormControl size="small" sx={{ minWidth: 120 }}><InputLabel id={`qty-${item.asset_id}`}>Периодов</InputLabel><Select labelId={`qty-${item.asset_id}`} aria-label={`Периодов для ${item.name}`} value={choice.qty || 1} label="Периодов" onChange={event => setExtendSelections(prev => ({ ...prev, [item.asset_id]: { ...choice, qty: event.target.value } }))}>{[1,2,3,4,5,6,7,8,9,10].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}</Select></FormControl>
                      <Typography variant="caption" color={BRAND.textSecondary} sx={{ width: '100%' }}>Новый срок: {newEnd ? newEnd.toLocaleDateString('ru-RU') : '—'}</Typography>
                    </Box>}
                  </Paper>;
                })}</Stack>
                <Paper elevation={0} sx={{ p: 2, mt: 2, bgcolor: '#f7f5f1', borderRadius: 2, border: '1px solid #e8e3dc', display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">К оплате:</Typography><Typography variant="h6" fontWeight={700} color={BRAND.primary}>{total} ₽</Typography></Paper>
              </Box>;
            })()}
            {extendOrder?.payment_provider === 'ozon' && <Box sx={{ mt: 2 }}>
              {currentExtensionQuote ? <Alert severity="info">
                По расчёту сервера к оплате: {currentExtensionQuote.amount} ₽.
                {currentExtensionQuote.items.map(item => <Typography key={item.asset_id} variant="body2">
                  {item.name}: до {formatDate(item.new_end_date)}
                </Typography>)}
                Продление вступит в силу после подтверждения оплаты Ozon.
                <RentalTerms terms={currentExtensionQuote.terms} />
              </Alert> : <Typography variant="body2">Проверьте окончательную сумму и сроки кнопкой «Рассчитать продление».</Typography>}
            </Box>}
            {extendOrder && extendOrder.payment_provider !== 'ozon' && <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color={BRAND.textSecondary} sx={{ mb: 1 }}>Способ оплаты через ЮKassa</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {[['sbp', 'СБП'], ['card', 'Карта'], ['sberpay', 'SberPay']].map(([value, label]) => <Button key={value} size="small" variant={extensionPaymentMethod === value ? 'contained' : 'outlined'} aria-pressed={extensionPaymentMethod === value} onClick={() => setExtensionPaymentMethod(value)}>{label}</Button>)}
              </Stack>
            </Box>}
            {error && <Alert severity="error">{error}</Alert>}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button disabled={loading} onClick={() => setExtendDialog(false)} sx={{ textTransform: 'none' }}>Отмена</Button>
            <Button variant="contained" onClick={() => handleExtendConfirm()}
              disabled={loading || (Array.isArray(extendOrder?.items) && extendOrder.items.length > 1
                ? !Object.values(extendSelections).some(value => value.selected && value.tariff_id)
                : !extendTariffId || !(extendOrder?.tariffs?.length))}
              sx={{ borderRadius: 2, textTransform: 'none', background: BRAND.gradient }}>
              {extendOrder?.payment_provider === 'ozon' && !currentExtensionQuote ? 'Рассчитать продление' : 'Оплатить и продлить'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );

  const renderOrderList = (list, showActions) => {
    if (list.length === 0) {
      return (
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: 3, border: '1px solid #e8e3dc' }}>
          <ReceiptIcon sx={{ fontSize: 56, color: '#dcd6cc', mb: 2 }} />
          <Typography color={BRAND.textSecondary}>Нет заказов</Typography>
        </Paper>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {list.map(order => {
          const st = STATUS_MAP[order.status] || { label: order.status_name || order.status, color: 'default' };
          const isPaid = order.payment_status === 'succeeded' || ['оплачена', 'в_процессе', 'активная'].includes(order.status);
          const itemTerms = Array.isArray(order.items) ? order.items.filter(item => item?.name) : [];
          const movements = [
            ...(order.payments || []).map(payment => ({ ...payment, isRefund: false })),
            ...(order.refunds || []).map(refund => ({ ...refund, isRefund: true })),
          ].sort((a, b) => (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0));
          return (
            <Paper key={order.id} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8e3dc', overflow: 'hidden' }}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>{order.asset_name || 'Оборудование'}</Typography>
                    {itemTerms.length > 1 && itemTerms.map(item => <Typography key={item.asset_id} variant="caption" display="block" color={BRAND.textSecondary}>
                      {item.name}{item.weeks ? ` — ${item.weeks} нед., до ${formatDate(item.end_date)}` : ''}{item.overdue_days ? ` · просрочено ${item.overdue_days} дн.${item.estimated_debt ? ` · к доплате сейчас ${item.estimated_debt} ₽` : ''}` : ''}
                    </Typography>)}
                    <Typography variant="body2" color={BRAND.textSecondary}>
                      Заказ {order.deal_number || order.id}
                    </Typography>
                  </Box>
                  <Chip label={st.label} color={st.color} size="small" sx={{ fontWeight: 600 }} />
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon sx={{ fontSize: 18, color: BRAND.textSecondary }} />
                    <Box>
                      <Typography variant="caption" color={BRAND.textSecondary}>Период</Typography>
                      <Typography variant="body2" noWrap>{formatDate(order.start_date)} &mdash; {formatDate(order.end_date)}</Typography>
                    </Box>
                  </Box>
                  {order.issued_at && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckIcon sx={{ fontSize: 18, color: BRAND.success }} />
                      <Box>
                        <Typography variant="caption" color={BRAND.textSecondary}>Товар выдан</Typography>
                        <Typography variant="body2">
                          {new Date(order.issued_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {order.delivery_type === 'delivery'
                      ? <DeliveryIcon sx={{ fontSize: 18, color: BRAND.textSecondary }} />
                      : <StoreIcon sx={{ fontSize: 18, color: BRAND.textSecondary }} />}
                    <Box>
                      <Typography variant="caption" color={BRAND.textSecondary}>
                        {order.delivery_type === 'delivery' ? 'Доставка' : 'Самовывоз'}
                      </Typography>
                      {(order.address || formatPickupLocation(order)) && (
                        <Typography variant="body2">{order.address || formatPickupLocation(order)}</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f7f5f1', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color={BRAND.textSecondary}>Сумма аренды:</Typography>
                    <Typography variant="h6" fontWeight={700} color={BRAND.primary}>
                      {order.total_amount ?? order.amount_due ?? 0} ₽
                    </Typography>
                  </Box>
                  {order.paid_total > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="body2" color={BRAND.textSecondary}>Оплачено:</Typography>
                      <Typography variant="body2" fontWeight={600} color={BRAND.success}>
                        {order.paid_total} ₽
                      </Typography>
                    </Box>
                  )}
                  {order.refunded_total > 0 && <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="body2" color={BRAND.textSecondary}>Возвращено:</Typography>
                    <Typography variant="body2" fontWeight={600}>{order.refunded_total} ₽</Typography>
                  </Box>}
                  {order.status === 'отменена' && order.paid_total > 0 && <Box sx={{ mt: 1 }}>
                    {order.refund_due > 0
                      ? <Alert severity="warning">Осталось вернуть: {order.refund_due} ₽. Возврат этой суммы ещё не подтверждён банком.</Alert>
                      : order.refunded_total >= order.paid_total && <Alert severity="success">Оплата возвращена полностью</Alert>}
                  </Box>}
                  {order.status !== 'отменена' && order.paid_total > 0 && (order.total_amount - (order.net_paid_total ?? order.paid_total)) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="body2" color={BRAND.textSecondary}>Остаток:</Typography>
                      <Typography variant="body2" fontWeight={600} color={BRAND.warning}>
                        {Number((order.total_amount - (order.net_paid_total ?? order.paid_total)).toFixed(2))} ₽
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {movements.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color={BRAND.textSecondary} fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      История оплат и возвратов
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {movements.map((p, i) => (
                        <Box key={`${p.isRefund ? 'refund' : 'payment'}-${p.id ?? i}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: i < movements.length - 1 ? '1px solid #f1eeea' : 'none' }}>
                          <Box>
                            <Typography variant="body2">{p.description || (p.isRefund ? 'Возврат оплаты' : 'Оплата')}</Typography>
                            {p.fiscal_receipts?.map(doc => <Typography key={doc.id} variant="caption" display="block">
                              Чек: ФД {doc.fiscal_document} · ФП {doc.fiscal_sign} · {doc.amount} ₽
                              {doc.url && <Button component="a" href={doc.url} target="_blank" rel="noopener noreferrer" size="small">Открыть чек</Button>}
                            </Typography>)}
                            <Typography variant="caption" color={BRAND.textSecondary}>
                              {p.date ? new Date(p.date).toLocaleDateString('ru-RU') : '—'}{p.method && p.method !== '—' ? ` · ${PAYMENT_METHOD_NAMES[p.method] || p.method}` : ''}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={700} color={p.isRefund ? BRAND.text : BRAND.success}>{p.isRefund ? '−' : '+'}{p.amount} ₽</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                <ReturnStatus info={order.return_info} token={portalToken} onChanged={goToPortal} />
                {Array.isArray(order.returns) && order.returns.length > 0 && itemTerms.length !== 1 && order.returns.map(info => <Box key={`return-${info.id}`}>
                  <Typography variant="body2" fontWeight={650} sx={{ mt: 2 }}>{info.asset_name}</Typography>
                  <ReturnStatus info={info} token={portalToken} onChanged={goToPortal} />
                </Box>)}
                {order.pickup_code && order.contract_token && !order.return_info && (
                  <PickupCodeBlock token={order.contract_token} session={portalToken} />
                )}

                {showActions && (
                  <Box className="cp-order-actions" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }}>
                    <Button size="small" variant="outlined" startIcon={<ContractIcon />}
                      onClick={() => { setSelectedOrder(order); setContractDialog(true); }}
                      sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>
                      Договор
                    </Button>

                    {order.status === 'заявка' && !isPaid && (
                      <Button size="small" variant="outlined" startIcon={<StorefrontIcon />}
                        onClick={() => editOrderInCatalog(order, order.id, order.deal_number)}
                        sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>
                        Изменить заявку
                      </Button>
                    )}

                    {order.can_cancel && (
                      <Button size="small" color="error" variant="text" onClick={async () => {
                        if (!window.confirm(`Отменить заявку №${order.deal_number || order.id}?`)) return;
                        try {
                          const { data } = await api.post(`/client-portal/orders/${order.id}/cancel`, { reason: 'Отменено клиентом' }, { params: { token: portalToken }, skipLoading: true });
                          clearOzonPayment(data.payment_refs);
                          setOrders(previous => previous.map(item => item.id === order.id
                            ? { ...item, status: 'отменена', status_name: 'Отменена', can_cancel: false } : item));
                          await goToPortal();
                        } catch (requestError) { setError(requestError.response?.data?.detail || 'Не удалось отменить заявку'); }
                      }}>Отменить заявку</Button>
                    )}

                    {order.issue_act && <Box sx={{ width: '100%' }}><details><summary>Акт выдачи №{order.issue_act.number}</summary><HandoverAct act={order.issue_act} /></details></Box>}
                    {order.extensions?.length > 0 && <ExtensionDocuments documents={order.extensions} />}

                    {!isPaid && order.status !== 'отменена' && (
                      <Button size="small" disabled={loading} variant="contained" color="primary" startIcon={<PaymentIcon />}
                        onClick={() => handlePayment(order)}
                        sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>
                        Оплатить
                      </Button>
                    )}

                    {isPaid && !order.return_info && (
                      <Button size="small" disabled={loading} variant="contained" startIcon={<ExtendIcon />}
                        onClick={() => {
                          setError(null);
                          setExtendOrder(order);
                          const firstTariff = (order.tariffs && order.tariffs[0]) || null;
                          setExtendTariffId(firstTariff ? firstTariff.id : null);
                          setExtendQty(1);
                          setExtendSelections(Object.fromEntries((order.items || []).map(item => [item.asset_id, {
                            selected: !item.return_info && Boolean(item.tariffs?.length),
                            tariff_id: item.tariffs?.[0]?.id || null,
                            qty: 1,
                          }])));
                          setExtensionPaymentMethod('sbp');
                          setExtendDialog(true);
                        }}
                        sx={{ borderRadius: 2, textTransform: 'none', flex: 1, background: BRAND.gradient }}>
                        Продлить
                      </Button>
                    )}
                    <ClientReturnButton order={order} token={portalToken} onChanged={goToPortal} />
                  </Box>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    );
  };

  /* ======== ORDER CONFIRM ======== */
  const renderOrderConfirm = () => {
    if (!pendingOrder) return (
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color={BRAND.textSecondary}>Загрузка заказа...</Typography>
      </Box>
    );

    const o = pendingOrder.order || {};
    const pickupLocation = formatPickupLocation(o);
    const orderItems = Array.isArray(o.items) ? o.items.filter(item => item?.name) : [];
    const fallbackSpecification = orderItems.length
      ? `№ | Товар | Срок аренды | Стоимость\n${orderItems.map((item, index) =>
          `${index + 1} | ${item.name} | ${item.weeks ? `${item.weeks} нед.` : `${formatDate(item.start_date)} — ${formatDate(item.end_date)}`} | ${item.amount ?? '—'} ₽`
        ).join('\n')}\nИТОГО: ${o.total ?? o.amount_due ?? 0} ₽`
      : `№ | Товар | Срок аренды | Стоимость\n1 | ${o.product_name || 'Оборудование'} | ${o.start_date || '—'} — ${o.end_date || '—'} | ${o.total ?? o.amount_due ?? 0} ₽\nИТОГО: ${o.total ?? o.amount_due ?? 0} ₽`;

    if (RECEIVED_ORDER_STATUSES.includes(pendingOrder.status)) return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e8e3dc', textAlign: 'center' }}>
        <CheckIcon sx={{ fontSize: 72, color: BRAND.success, mb: 2 }} />
        <Typography variant="h5" fontWeight={700}>Товар получен</Typography>
        <Typography color={BRAND.textSecondary} sx={{ mt: 1 }}>Заказ {pendingOrder.deal_number}</Typography>
        <Typography sx={{ mt: 2 }}>{o.product_name || 'Оборудование'}</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>{COMPLETED_ORDER_STATUSES.includes(pendingOrder.status) ? 'Аренда завершена.' : 'Аренда началась. Код получения больше не нужен.'}</Typography>
        {client && <Button fullWidth variant="contained" onClick={goToPortal} sx={{ mt: 3 }}>Личный кабинет</Button>}
      </Paper>
    );

    if (pickupCode || pendingOrder.status === 'оплачена') return (
      <Fade in>
        <Box className="cp-step-content">
          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e8e3dc', textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 72, color: BRAND.success, mb: 2 }} />
            <Typography variant="h5" fontWeight={700}>Оплата прошла!</Typography>
            <Typography variant="body2" color={BRAND.textSecondary} sx={{ mb: 3 }}>
              Заказ {pendingOrder.deal_number}
            </Typography>

            <Box sx={{ mb: 3 }}><PickupCodeBlock token={orderToken} session={portalToken} /></Box>

            <Alert severity="info" sx={{ textAlign: 'left', borderRadius: 2 }}>
              <strong>Что дальше:</strong><br />
              {'\u2022'} Приходите на пункт выдачи<br />
              {'\u2022'} Введите телефон и код на постамате или назовите код сотруднику<br />
              {'\u2022'} Получите товар
            </Alert>

            {client && (
              <Button fullWidth variant="contained" size="large" onClick={goToPortal}
                sx={{ mt: 3, py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600, background: BRAND.gradient }}>
                Личный кабинет
              </Button>
            )}
          </Paper>
        </Box>
      </Fade>
    );

    if (offerAccepted || pendingOrder.offer_accepted) return (
      <Fade in>
        <Box className="cp-step-content">
          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e8e3dc' }}>
            <Typography component="h1" variant="h5" fontWeight={700} sx={{ mb: 3 }}>{' \uD83D\uDCB3'} Оплата</Typography>

            <Paper elevation={0} sx={{ p: 2.5, mb: 3, bgcolor: '#f7f5f1', borderRadius: 2, border: '1px solid #e8e3dc' }}>
              <Typography variant="subtitle1" fontWeight={600}>{o.product_name || 'Товар'}</Typography>
              <Typography variant="body2" color={BRAND.textSecondary}>{pendingOrder.deal_number}</Typography>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>К оплате:</Typography>
                <Typography variant="h5" fontWeight={700} color={BRAND.primary}>{o.total ?? o.amount_due ?? 0} ₽</Typography>
              </Box>
            </Paper>

            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>Оферта принята {'\u2705'}</Alert>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {paymentUncertain && !payCardDialog && <Alert severity="info" sx={{ mb: 2 }}>{PAYMENT_UNCERTAIN_TEXT}</Alert>}

            <Button fullWidth variant="contained" size="large" disabled={loading}
              onClick={() => { setError(null); if (paymentUncertain) checkPayment(); else setPayCardDialog(true); }}
              sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600, background: BRAND.gradient }}>
              {loading ? 'Обработка...' : paymentUncertain ? 'Проверить оплату' : `Перейти к оплате ${o.total ?? o.amount_due ?? 0} ₽`}
            </Button>
            {paymentUncertain && !payCardDialog && pendingOrder.payment_provider === 'ozon' && <Button fullWidth disabled={loading}
              onClick={() => payOrder(paymentMethod, true)} sx={{ mt: 1 }}>Продолжить начатую оплату</Button>}
          </Paper>

          <Dialog open={payCardDialog} onClose={() => !loading && setPayCardDialog(false)} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaymentIcon sx={{ color: BRAND.primary }} /> Оплата
            </DialogTitle>
            <DialogContent>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                {pendingOrder?.payment_provider === 'ozon' ? 'Вы перейдёте на защищённую страницу оплаты. Способ оплаты выберите там.' : 'Демонстрационный платёжный агрегатор — реального списания нет.'}
              </Alert>
              {pendingOrder?.payment_provider !== 'ozon' && <Typography variant="body2" color={BRAND.textSecondary} sx={{ mb: 1.5 }}>Выберите способ оплаты</Typography>}
              <Stack spacing={1.25} sx={{ display: pendingOrder?.payment_provider === 'ozon' ? 'none' : 'flex' }}>
                {[
                  ['sbp', 'СБП', 'QR-код или приложение вашего банка'],
                  ['card', 'Банковская карта', 'Реквизиты вводятся на стороне агрегатора'],
                  ['sberpay', 'SberPay', 'Подтверждение в приложении СберБанк Онлайн'],
                ].map(([value, title, description]) => (
                  <Button key={value} variant={paymentMethod === value ? 'contained' : 'outlined'}
                    onClick={() => setPaymentMethod(value)} aria-pressed={paymentMethod === value}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left', px: 2, py: 1.25 }}>
                    <Box><Typography fontWeight={700}>{title}</Typography><Typography variant="caption" sx={{ opacity: 0.8 }}>{description}</Typography></Box>
                  </Button>
                ))}
              </Stack>
              {error && <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>{error}</Alert>}
              {paymentUncertain && <Alert severity="info" sx={{ mt: 1 }}>{PAYMENT_UNCERTAIN_TEXT}</Alert>}
              {paymentUncertain && pendingOrder.payment_provider === 'ozon' && <Button fullWidth disabled={loading}
                onClick={() => payOrder(paymentMethod, true)} sx={{ mt: 1 }}>Продолжить начатую оплату</Button>}
              {missingPaymentRef && <FormControlLabel control={<Checkbox checked={noDebitConfirmed} onChange={event => setNoDebitConfirmed(event.target.checked)} />} label="По прежнему платежу деньги не списывались. Создать новую ссылку для этого заказа." />}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setPayCardDialog(false)} disabled={loading} sx={{ textTransform: 'none' }}>
                Отмена
              </Button>
              <Button variant="contained" onClick={() => payOrder(paymentMethod)} disabled={loading || (!paymentUncertain && Boolean(missingPaymentRef) && !noDebitConfirmed)}
                sx={{ borderRadius: 2, textTransform: 'none', background: BRAND.gradient }}>
                {loading ? <CircularProgress size={20} color="inherit" /> : paymentUncertain ? 'Проверить оплату' : missingPaymentRef ? 'Создать новую ссылку оплаты' : 'Оплатить'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Fade>
    );

    return (
      <Fade in>
        <Box className="cp-step-content">
          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e8e3dc' }}>
            <Typography component="h1" variant="h5" fontWeight={700} sx={{ mb: 3 }}>
              Заявка {pendingOrder.deal_number}
            </Typography>

            <Paper elevation={0} sx={{ p: 2.5, mb: 3, bgcolor: '#f7f5f1', borderRadius: 2, border: '1px solid #e8e3dc' }}>
              {orderItems.length > 1 ? <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                {orderItems.map(item => <Box key={item.asset_id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700}>{item.name}</Typography>
                  <Typography variant="body2" color={BRAND.textSecondary} sx={{ whiteSpace: 'nowrap' }}>
                    {item.weeks ? `${item.weeks} нед.` : ''}{item.end_date ? ` · до ${formatDate(item.end_date)}` : ''}
                  </Typography>
                </Box>)}
              </Stack> : <Typography variant="subtitle1" fontWeight={600} gutterBottom>{o.product_name || 'Товар'}</Typography>}

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, my: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon sx={{ fontSize: 18, color: BRAND.textSecondary }} />
                  <Box>
                    <Typography variant="caption" color={BRAND.textSecondary}>Начало</Typography>
                    <Typography variant="body2">{o.start_date || '\u2014'}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon sx={{ fontSize: 18, color: BRAND.textSecondary }} />
                  <Box>
                    <Typography variant="caption" color={BRAND.textSecondary}>{orderItems.length > 1 ? 'Последнее окончание' : 'Окончание'}</Typography>
                    <Typography variant="body2">{o.end_date || '\u2014'}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {o.delivery_type === 'delivery'
                    ? <DeliveryIcon sx={{ fontSize: 18, color: BRAND.textSecondary }} />
                    : <StoreIcon sx={{ fontSize: 18, color: BRAND.textSecondary }} />}
                  <Box>
                    <Typography variant="caption" color={BRAND.textSecondary}>Получение</Typography>
                    <Typography variant="body2">
                      {o.delivery_type === 'delivery' ? 'Доставка' : 'Самовывоз'}
                    </Typography>
                  </Box>
                </Box>
                {o.delivery_type !== 'delivery' && pickupLocation && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, gridColumn: '1 / -1' }}>
                    <StoreIcon sx={{ fontSize: 18, color: BRAND.textSecondary }} />
                    <Box>
                      <Typography variant="caption" color={BRAND.textSecondary}>Пункт выдачи</Typography>
                      <Typography variant="body2">{pickupLocation}</Typography>
                    </Box>
                  </Box>
                )}
                {o.address && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeliveryIcon sx={{ fontSize: 18, color: BRAND.textSecondary }} />
                    <Box>
                      <Typography variant="caption" color={BRAND.textSecondary}>Адрес</Typography>
                      <Typography variant="body2">{o.address}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                <Typography variant="h6">Итого:</Typography>
                <Typography variant="h5" fontWeight={700} color={BRAND.primary}>{o.total ?? o.amount_due ?? 0} ₽</Typography>
              </Box>
            </Paper>

            <Typography variant="body2" color={BRAND.textSecondary} sx={{ mb: 1 }}>Ваши данные:</Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>{o.full_name}, {o.phone}</Typography>

            <Button fullWidth variant="outlined" startIcon={<StorefrontIcon />}
              onClick={() => editOrderInCatalog(o, pendingOrder.deal_id, pendingOrder.deal_number)}
              sx={{ mb: 2, borderRadius: 2, textTransform: 'none' }}>
              Изменить товары и сроки
            </Button>

            <Paper variant="outlined" sx={{
              mb: 3, p: 2, maxHeight: showContract ? 360 : 64, overflow: 'auto',
              transition: 'max-height 0.3s', cursor: 'pointer', borderRadius: 2, bgcolor: '#FAFAFA',
            }} role="button" tabIndex={0} aria-expanded={showContract} aria-label="Договор-оферта" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowContract(!showContract); } }} onClick={() => setShowContract(!showContract)}>
              <Typography variant="subtitle2">
              {'\uD83D\uDCDC'} Договор-оферта {showContract ? '▲' : '▼'}
              </Typography>
              {showContract && (
                <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', mt: 1 }}>
{pendingOrder.offer_text
  ? pendingOrder.offer_text
  : `ДОГОВОР-ОФЕРТА АРЕНДЫ №${pendingOrder.deal_number}

1. ПРЕДМЕТ ДОГОВОРА
Арендодатель передаёт, а Арендатор принимает во временное пользование: ${o.product_name || 'Оборудование'}
${o.delivery_type !== 'delivery' && pickupLocation ? `Место выдачи: ${pickupLocation}` : ''}

2. СРОКИ АРЕНДЫ
Начало: ${o.start_date || '—'}  Окончание: ${o.end_date || '—'}

3. СТОИМОСТЬ И ПОРЯДОК РАСЧЁТОВ
${fallbackSpecification}
Оплата производится онлайн.

4. КОД ВЫДАЧИ
После оплаты вы получите код для получения.

5. ОТВЕТСТВЕННОСТЬ
Арендатор обязуется вернуть имущество в исправном состоянии.

Условия проката: /docs/offer. Акцепт — отдельное подтверждение условий заказа.`}
                </Typography>
              )}
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            <LegalAcceptance value={orderLegal} onChange={setOrderLegal} offer />
            <Button fullWidth variant="contained" color="primary" size="large" disabled={loading || !legalReady(orderLegal)}
              onClick={acceptOffer}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
              sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              {loading ? 'Обработка...' : 'Принять оферту и перейти к оплате'}
            </Button>
          </Paper>
        </Box>
      </Fade>
    );
  };

  /* ======== Handlers ======== */
  const handlePayment = async (order) => {
    if (!order?.contract_token) {
      setError('Для этого заказа оплата онлайн недоступна. Обратитесь к менеджеру.');
      return;
    }
    setError(null);
    navigate(`/my?order=${encodeURIComponent(order.contract_token)}`);
  };

  const handleExtendConfirm = async () => {
    if (extensionPending.current) return;
    const multiItem = Array.isArray(extendOrder?.items) && extendOrder.items.length > 1;
    const tariff = (extendOrder?.tariffs || []).find(t => t.id === extendTariffId);
    const selectedItems = multiItem ? extendOrder.items.filter(item => extendSelections[item.asset_id]?.selected).map(item => ({
      asset_id: item.asset_id,
      tariff_id: extendSelections[item.asset_id].tariff_id,
      qty: extendSelections[item.asset_id].qty,
    })) : [];
    if ((!multiItem && !tariff) || (multiItem && !selectedItems.length)) {
      setError('Выберите товар и тариф');
      return;
    }
    extensionPending.current = true;
    setLoading(true);
    setError('');
    try {
      const orderToken = extendOrder?.contract_token;
      if (!orderToken) throw new Error('Нет токена заказа');
      const payload = multiItem ? { items: selectedItems } : { tariff_id: tariff.id, qty: extendQty };
      if (extendOrder.payment_provider === 'ozon') {
        const storageKey = `prokatprost_extension_${extendOrder.id}`;
        let previous;
        try { previous = JSON.parse(localStorage.getItem(storageKey)); } catch { /* Invalid storage never confirms payment. */ }
        if (!currentExtensionQuote) {
          const quote = previous?.signature === extensionSignature && previous?.quote && previous?.payload
            ? previous.quote : (await api.post(`/client-portal/order/${orderToken}/extension-quote`, payload, portalRequest(portalToken))).data;
          setExtensionQuote({ signature: extensionSignature, data: quote });
          return;
        }
        const attempt = previous?.signature === extensionSignature && previous?.payload
          ? previous : { signature: extensionSignature, quote: currentExtensionQuote, payload: {
            ...payload, quote_hash: currentExtensionQuote.quote_hash, quoted_at: currentExtensionQuote.quoted_at,
            ...(currentExtensionQuote.terms ? { terms_version: currentExtensionQuote.terms.version } : {}),
            idempotency_key: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          } };
        localStorage.setItem(storageKey, JSON.stringify(attempt));
        const { data } = await api.post(`/client-portal/order/${orderToken}/extend`, attempt.payload, portalRequest(portalToken));
        if (data.confirmation_url) { openOzonPayment(data); return; }
        if (data.success && data.status === 'paid') {
          localStorage.removeItem(storageKey); setExtensionQuote(null); setExtendDialog(false);
          const refreshed = await api.get('/client-portal/orders', { params: { token: portalToken }, skipLoading: true });
          setOrders(refreshed.data.orders || []);
          return;
        }
        if (['canceled', 'test_paid', 'refunded', 'test_refunded'].includes(data.status)) {
          localStorage.removeItem(storageKey); setExtensionQuote(null);
        }
        setError(ozonStatusText[data.status] || 'Проверяем оплату продления. Повторно не оплачивайте.');
        return;
      }
      payload.provider = 'yookassa_demo';
      payload.payment_method = extensionPaymentMethod;
      payload.idempotency_key = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
      const res = await api.post(`/client-portal/order/${orderToken}/extend`, payload, portalRequest(portalToken));
      const newEnd = res.data?.new_end_date
        ? new Date(res.data.new_end_date).toLocaleDateString('ru-RU')
        : '';
      const newPayment = res.data?.payment;
      // Локально обновим заказ: новая дата окончания, новая сумма,
      // и добавляем платёж в историю + увеличиваем paid_total.
      setOrders(prev => prev.map(o => {
        if (o.id !== extendOrder.id) return o;
        const prevPayments = Array.isArray(o.payments) ? o.payments : [];
        const payments = newPayment
          ? [newPayment, ...prevPayments]
          : prevPayments;
        const paid_total = Number(o.paid_total || 0) + Number(newPayment?.amount ?? res.data?.amount ?? 0);
        return {
          ...o,
          end_date: res.data?.new_end_date || o.end_date,
          total_amount: res.data?.total_amount ?? o.total_amount,
          amount: res.data?.total_amount ?? o.amount,
          payments,
          paid_total,
          extensions: res.data?.extension_document
            ? [...(o.extensions || []).filter(doc => doc.id !== res.data.extension_document.id), res.data.extension_document]
            : o.extensions,
        };
      }));
      setExtendDialog(false);
      alert(`Аренда продлена до ${newEnd}. Оплачено: ${res.data?.amount} ₽`);
    } catch (err) {
      if (err.response?.status === 401) { requireOrderLogin(extendOrder?.contract_token); return; }
      if (extendOrder?.payment_provider === 'ozon' && ['Расчёт продления устарел', 'Условия продления изменились'].some(text => err.response?.data?.detail?.includes(text))) {
        localStorage.removeItem(`prokatprost_extension_${extendOrder.id}`);
        setExtensionQuote(null);
      }
      setError(err.response?.data?.detail || 'Не удалось продлить аренду');
    } finally {
      extensionPending.current = false;
      setLoading(false);
    }
  };

  /* ======== MAIN RENDER ======== */
  const isPortal = step === PORTAL_STEP;
  const journeyStage = step === AUTH_STEP || step === BANK_ID_STEP || step === CODE_STEP ? 0
    : step === ONBOARD_STEP ? 1
    : pickupCode || pendingOrder?.status === 'оплачена' || RECEIVED_ORDER_STATUSES.includes(pendingOrder?.status) ? 3 : 2;
  const journeyItem = pendingItemName || pendingOrder?.order?.product_name;
  const journeyLabels = ['Вход', 'Ваши данные', 'Оформление', 'Получение'];

  return (
    <ThemeProvider theme={portalTheme}>
    <Box className="cp-page">
      <header className="cp-header">
        <Container maxWidth="lg" className="cp-header-inner">
          <RouterLink to="/" className="cp-logo" aria-label="ПрокатПрост — главная">
            <span className="cp-logo-mark"><StorefrontIcon /></span>
            Прокат<span>Прост</span><span className="cp-logo-dot">.</span>
          </RouterLink>
          {client && !isPortal
            ? <Button onClick={goToPortal} startIcon={<PersonIcon />}>Мои заказы</Button>
            : <span className="cp-header-label"><PersonIcon /> Личный кабинет</span>}
          <Button component={RouterLink} to="/" startIcon={<ArrowBackIcon />} className="cp-catalog-link">Вернуться в каталог</Button>
        </Container>
      </header>
      <Container maxWidth="lg" component="main" className={`cp-layout${isPortal ? ' cp-layout--portal' : ''}`}>
        {!isPortal && <aside className="cp-journey">
          <span className="cp-eyebrow">ВАША АРЕНДА НАЧИНАЕТСЯ ЗДЕСЬ</span>
          <h2>Всё просто.<br /><span>И всё под рукой.</span></h2>
          <p>Выберите срок, оформите аренду<br className="cp-desktop-break" /> и заберите нужную вещь.</p>
          {journeyItem && <div className="cp-item-summary">
            <span className="cp-item-icon"><InventoryIcon /></span>
            <div><small>Вы выбрали</small><strong>{journeyItem}</strong>{pendingOrder?.order?.items?.length
              ? <button type="button" onClick={() => editOrderInCatalog(pendingOrder.order, pendingOrder.deal_id, pendingOrder.deal_number)}>Изменить выбор <ArrowForwardIcon /></button>
              : <RouterLink to={`/?restoreItem=${encodeURIComponent(journeyItem)}`}>Изменить выбор <ArrowForwardIcon /></RouterLink>}</div>
          </div>}
          <ol className="cp-progress" aria-label="Этапы аренды">
            {journeyLabels.map((label, index) => <li key={label} className={index === journeyStage ? 'is-current' : index < journeyStage ? 'is-complete' : ''} aria-current={index === journeyStage ? 'step' : undefined}>
              <span>{index < journeyStage ? <CheckIcon /> : String(index + 1).padStart(2, '0')}</span><b>{label}</b>
            </li>)}
          </ol>
          <div className="cp-journey-note"><LockOutlinedIcon /><span>Заказы, сроки и документы —<br />в вашем личном кабинете.</span></div>
        </aside>}
        <div className={isPortal ? 'cp-dashboard' : 'cp-stage'}>
        {step === PORTAL_STEP && ozonStatusText[ozonStatus] && <Alert
          severity={['paid', 'refunded', 'partially_refunded'].includes(ozonStatus) ? 'success' : ['review', 'canceled', 'not_found', 'paid_after_cancel'].includes(ozonStatus) ? 'warning' : 'info'}
          sx={{ mb: 2, borderRadius: 2 }}>{ozonStatusText[ozonStatus]}</Alert>}
        {loading && step === AUTH_STEP && token ? <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /><Typography sx={{ mt: 2 }}>Загружаем ваш кабинет…</Typography></Box> : <>
        {step === AUTH_STEP && renderAuth()}
        {step === BANK_ID_STEP && renderBankIdDemo()}
        {step === CODE_STEP && renderCode()}
        {step === ONBOARD_STEP && renderOnboard()}
        {step === PORTAL_STEP && renderPortal()}
        {step === ORDER_CONFIRM_STEP && renderOrderConfirm()}
        {step === 'not_client' && (
          <Fade in>
            <Box className="cp-step-content">
              <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid #e8e3dc' }}>
                <Typography sx={{ fontSize: 56, mb: 2 }}>{'\uD83E\uDD37'}</Typography>
                <Typography variant="h5" fontWeight={700} gutterBottom>Вы пока не наш клиент</Typography>
                <Typography variant="body2" color={BRAND.textSecondary} sx={{ mb: 3 }}>
                  {notClientMessage || 'Номер не найден в базе.'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexDirection: 'column' }}>
                  <Button variant="contained" onClick={() => { setStep(AUTH_STEP); setNotClientMessage(null); }}
                    sx={{ borderRadius: 2, textTransform: 'none', background: BRAND.gradient }}>
                    Попробовать другой номер
                  </Button>
                  <Button variant="outlined" href="https://lmedservice.ru" target="_blank"
                    sx={{ borderRadius: 2, textTransform: 'none' }}>
                    Оформить аренду
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Fade>
        )}
        </>}
        </div>
      </Container>
      <footer className="cp-footer">
        <Container maxWidth="lg">
          <span>© {new Date().getFullYear()} ПрокатПрост</span>
          <LegalLinks />
        </Container>
      </footer>
    </Box>
    </ThemeProvider>
  );
}

export default ClientPortal;
